import { AfterViewInit, Component, DoCheck, ElementRef, HostListener, OnDestroy, OnInit, QueryList, Renderer2, ViewChild, ViewChildren, ChangeDetectorRef } from '@angular/core';
import { IonTextarea, ModalController, Platform } from '@ionic/angular';
import { AppState } from 'src/app/app.service';
import { HamburgerMenuComponent } from '../hamburger-menu/hamburger-menu.component';
import { FooziAiLibraryPopupComponent } from '../foozi-ai-library-popup/foozi-ai-library-popup.component';
import { AnimationsService } from 'src/app/shared/animations.service';
import { FooziAiConciergeService } from '../foozi-ai-concierge.service';
import { Keyboard } from '@awesome-cordova-plugins/keyboard/ngx';
import { FullScreenRoutineComponent } from 'src/app/full-screen-routine/full-screen-routine.component';
import { duration } from 'moment';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Clipboard } from '@ionic-native/clipboard/ngx';
import { ToasterService } from 'src/app/toaster.service';
import mixpanel from 'mixpanel-browser';
import { FullScreenFeedComponent } from 'src/app/full-screen-feed/full-screen-feed.component';
import { MobileChatBoardComponent } from 'src/app/pages/mobile-chat-board/mobile-chat-board.component';
import { HomeUiService } from '../home-ui.service';
import { takeUntil, catchError, map, switchMap, take } from 'rxjs/operators';
import { Subject, of, Observable } from 'rxjs';
import { MicroActionStateService, MicroActionSessionState } from 'src/app/micro-action-chatbot-v1/micro-action-state.service';
import {
  FooziHistoryModalComponent,
  FooziHistorySelection,
  HISTORY_DISMISS_CURRENT,
} from '../foozi-history-modal/foozi-history-modal.component';
import { OPEN_ACTION_AFTER_SWITCH_KEY } from '../micro-action-history-modal/micro-action-history-modal.component';
import { KyperDataApiService } from 'src/app/services/kyper-data-api.service';
import { FullScreenVideoComponent } from 'src/app/full-screen-video/full-screen-video.component';
import { FullScreenAudioComponent } from 'src/app/full-screen-audio/full-screen-audio.component';
import { FullScreenDocumentComponent } from 'src/app/my-library/full-screen-document.component';
import { I18n } from 'src/app/i18n.service';
import { RoutineV2Service } from 'src/app/routine-v2/routine-v2.service';
import { HomeV5Service } from 'src/app/pages/home-v5/home-v5.service';
import { AlertWebactionCalendarComponent } from 'src/app/alert-webaction-calendar/alert-webaction-calendar.component';
import { CordovaService } from 'src/app/cordova.service';
import { CalendarService } from 'src/app/calendar.service';
import { CompletedReviewModalComponent } from 'src/app/completed-review-modal/completed-review-modal.component';
import { markdownToHtml } from 'src/app/markdown.pipe';
import { LibraryService } from 'src/app/my-library/library.service';

/**
 * The questions offered on a blank panel.
 *
 * Client-side on purpose: the server's suggested_questions come back with a session, and a fresh
 * panel deliberately has none until the learner's first message. These are what the greeting has
 * always shown — a session's own suggestions replace them once one is resumed.
 */
/**
 * How the panel was left, so a refresh or a path switch comes back to it rather than to whatever
 * the server last saw.
 *
 *   'fresh'   the greeting and its questions, no conversation started
 *   'blank'   the header's new-chat button — nothing on screen at all
 *   'session' a conversation is live; its id is in foozi_session
 *
 * sessionStorage is exactly the right lifetime: it survives a refresh and a path switch (same tab),
 * and is gone when the tab is closed — which is when a fresh state is wanted. It is also cleared on
 * logout, so the next login does not inherit the last one's conversation.
 */
const PANEL_MODE_KEY = 'foozi_panel_mode';
/** The longest message the chat endpoint takes (its 422 says max_length 2000). */
export const MAX_MESSAGE_CHARS = 2000;
/** How long the "limit reached" notice stays up after an attempt to go past it. */
export const MESSAGE_LIMIT_NOTICE_MS = 4000;

const isHighSurrogate = (text: string, index: number) => /[\uD800-\uDBFF]/.test(text.charAt(index));
const isLowSurrogate = (text: string, index: number) => /[\uDC00-\uDFFF]/.test(text.charAt(index));

/**
 * The longest start of `text` that fits in `room` UTF-16 code units (the unit of the 2000 limit and
 * of string positions) without splitting a character: whole grapheme clusters where the platform can
 * tell them (an emoji with a skin tone, or a family joined by zero-width joiners, stays one piece),
 * else whole code points — never half of a surrogate pair, which would show and send as a broken
 * character.
 */
export function wholeCharactersWithin(text: string, room: number): string {
  if (room <= 0 || !text) { return ''; }
  if (text.length <= room) { return text; }
  const Segmenter = (Intl as any)?.Segmenter;
  const pieces: string[] = Segmenter
    ? Array.from(new Segmenter(undefined, { granularity: 'grapheme' }).segment(text), (part: any) => part.segment)
    : Array.from(text);
  let kept = '';
  for (const piece of pieces) {
    if (kept.length + piece.length > room) { break; }
    kept += piece;
  }
  return kept;
}
/**
 * Whether THIS conversation shows the greeting block — the "I'm Kyper Coach…" text and the chips
 * rendered inside it.
 *
 * Separate from the mode because the two answer different questions and stop agreeing the moment the
 * learner speaks: the mode becomes 'session' on the first message, and with it went the only record
 * of whether the conversation began on the blank screen or on a fresh launch. A refresh then put the
 * greeting back on a conversation the learner had deliberately started clean.
 */
const PANEL_GREETING_KEY = 'foozi_panel_greeting';
/**
 * What was decided for the conversation currently on screen, as `<session id>:on|off`.
 *
 * PANEL_GREETING_KEY above answers "which screen is this learner on", which is the right question
 * while no conversation is loaded. It is the wrong one afterwards: a cycle switch rebuilds this
 * component around whatever conversation was open, and that may be one reopened from History with
 * its greeting long since behind it. Keyed by id, so putting a conversation back always puts back
 * what was above it — and a different conversation is decided afresh rather than inheriting it.
 *
 * Superseded by `show_default_questions`, which the session now carries: this is consulted only for
 * conversations opened before that flag existed. See greetingFor.
 */
const PANEL_GREETING_SESSION_KEY = 'foozi_panel_greeting_session';
/**
 * Conversations that were started on the blank screen, by session id.
 *
 * localStorage rather than sessionStorage: History reaches conversations from earlier app runs, and
 * for a conversation opened before `show_default_questions` shipped this is the only record of how
 * one of them began — the server answers it for every session created since, and nothing else about
 * the response does: GET /session returns the same default suggested_questions either way, so "the
 * response carries questions" says nothing about whether the learner was ever shown any.
 *
 * Only the blank ones are listed, the greeting being the default, and the list is capped. An id that
 * falls off the end reads as an ordinary conversation, which is the harmless direction to be wrong
 * in: a greeting appears where none was asked for, rather than a conversation losing one.
 */
const BLANK_STARTED_KEY = 'foozi_blank_sessions';
const BLANK_STARTED_MAX = 100;
type PanelMode = 'blank' | 'fresh' | 'session';

const DEFAULT_SUGGESTIONS = [
  'Get tips on giving effective feedback',
  'Learn about goal setting in our company context',
  'Summarize the steps I have completed',
];

@Component({
  selector: "app-foozi-ai-concierge",
  templateUrl: "./foozi-ai-concierge.component.html",
  styleUrls: ["./foozi-ai-concierge.component.scss"],
})
export class FooziAiConciergeComponent implements OnInit, OnDestroy, AfterViewInit, DoCheck {
  readonly maxMessageChars = MAX_MESSAGE_CHARS;
  /** "You've reached the limit" — shown for a few seconds after an attempt to go past it. */
  showMessageLimitNotice = false;
  private messageLimitNoticeTimer: ReturnType<typeof setTimeout> | null = null;
  isMobile;
  isModalOpen;
  isDisableLike = false;
  isCopyContent = false;
  isMsgSaved;
  isLikeSelected = false;
  /**
   * The message being written. Every write of a value within the limit (typing, a cleared box after
   * sending, a suggested question) is remembered as the last draft, so a mobile insertion that goes
   * past the limit can be cut back to the draft without losing any of it — see capMessageLength.
   */
  get postMsg(): any { return this._postMsg; }
  set postMsg(value: any) {
    this._postMsg = value;
    const text = typeof value === 'string' ? value : '';
    if (text.length <= this.maxMessageChars) { this.lastWithinLimit = text; }
  }
  private _postMsg: any = '';
  private lastWithinLimit = '';
  chat: any = [];
  suggestions = [];
  isLoader: boolean;
  dislikeFeedback: boolean = false;
  touchStartY;
  rowCount = 1;
  messages= [];
  private timings: number[] = [];
  private typingDelay = 50;
  private messagePause = 1000; 
  private currentMessageIndex = 0;
  showFakeLastMessage: boolean = true;
  post_event_listeners = [];
  isKeyboardVisible: boolean = false;
  currentKeyboardHeight: number = 0;
   @ViewChildren("fooziDoc") fooziDoc:QueryList<ElementRef>;
  @ViewChild("hamModal") hamModal: HamburgerMenuComponent;
  @ViewChild('scrollBottom') private scrollBottom: ElementRef;
  @ViewChild('foozicardContainer') foozicardContainer: ElementRef;
  @ViewChild('textarea', { static: false }) textarea: IonTextarea;
  destroyed$ = new Subject<boolean>();
  isSessionRefreshing = false;
  isSessionInitializing = true;
  private serverMessageCount = 0;
  /**
   * True between clearing the panel and its first message, while the panel deliberately has no
   * conversation of its own. See ensureSession.
   */
  private wantsNewConversation = false;
  /**
   * Whether this conversation opened greeted — a fresh app open, or a session whose
   * `show_default_questions` is on — rather than from the header's new chat. It now decides only
   * whether the Coach introduces itself (see isFirstGreeting); suggested questions show regardless.
   */
  showWelcome = true;
  /**
   * Set when the conversation on screen was reopened from History, which is never a first meeting —
   * see isFirstGreeting.
   */
  private openedFromHistory = false;
  private serverMessageSession = '';
  private pageLeaveSetup = false;
  videoFormats = ['.mp4', '.mov', '.avi', '.avchd', '.f4v', '.mkv', '.wmv','youtube.com', 'webm', 'html5', 'HTML5', 'watch'];
  audioFormats = [".mp3"];
  casulaDocumentsList = [];
  path;
  loaded;
  showAnimation = false;
  fooziTextAreaLabel;
  microActionState: MicroActionSessionState | null = null;
  microActionDescHtml: SafeHtml | null = null;
  fooziRecommitAccepted = false;
  fooziRecommitDeclined = false;
  isMicroActionRefineMode = false;
  hasNativeCalendar = false;
  private activeTypingInterval: ReturnType<typeof setInterval> | null = null;
  /**
   * The cycle whose document library this panel has loaded, or null before it has loaded one.
   *
   * Not a cache for its own sake. `casualDocuments` in appState is a shared bag with no record of
   * which cycle filled it: one switch path clears it, another sets `currentPath` and leaves the
   * previous cycle's documents sitting there. Taking it at face value showed a conversation the
   * attachment it had in the cycle the learner just left. This panel matches only against a library
   * it fetched for the cycle the learner is in now.
   */
  private casualLibraryPathId: any = null;
  /**
   * The exact array this panel fetched, so a bag holding something else again is noticed.
   *
   * The cycle id alone says only that a library was fetched for this cycle at some point, and on
   * web the panel outlives the switch: fetch for cycle 5, move to 6 where app.component refills the
   * shared list, move back to 5 by the path that does not clear it, and the id still matched while
   * the list underneath was cycle 6's. The same id also went on matching after a switch emptied the
   * list, which dropped attachments with nothing left to trigger a reload.
   */
  private casualLibrary: any[] | null = null;
  /** Which transcript asked for documents last, so a library arriving late cannot fill another. */
  private documentsRequestId = 0;
  private suggestionsRequestId = 0;
  /**
   * The cycle the chips on screen were last asked for; undefined until they have been asked for once.
   *
   * On web the panel outlives a cycle switch — it stays mounted beside the home screen, so ngOnInit
   * never runs again — and `currentPath` is replaced some time after the switch, which also makes
   * loadSuggestedQuestions drop a reply already in flight. Compared in ngDoCheck so the new cycle's
   * chips are asked for as soon as the new cycle is in place.
   */
  private suggestionsPathId: any = undefined;
  /**
   * Whether the conversation on screen has settled, so its suggested questions can be asked for.
   * False while a session is still loading — a reply then would land on the conversation being
   * replaced. Nothing asks GET /suggested_questions without this.
   */
  private suggestionsAllowed = false;

  constructor(
    public platform: Platform,
    public appState: AppState,
    private modalController: ModalController,
    private animationService: AnimationsService,
    private keyboard: Keyboard,
    private renderer: Renderer2,
    private el: ElementRef,
    private sanitizer: DomSanitizer,
    private clipboard: Clipboard,
    private toaster: ToasterService,
    private fooziAiConciergeService: FooziAiConciergeService,
    private homeUiService: HomeUiService,
    public i18n: I18n,
    private cdRef: ChangeDetectorRef,
    private microActionStateService: MicroActionStateService,
    private kyperApiService: KyperDataApiService,
    private routineV2Service: RoutineV2Service,
    private homeV5Service: HomeV5Service,
    private cordovaService: CordovaService,
    private calendarService: CalendarService,
    private libraryService: LibraryService,
  ) {
    this.isMobile = this.appState.get("isMobile");
    this.isModalOpen = this.appState.get("isHamburgerOpen");
    this.path = this.appState.get('currentPath');
    const w: any = window;
    this.hasNativeCalendar = this.cordovaService.onCordova && w && w.plugins && w.plugins.calendar;
    let loaderMessages = [ 'Interpreting your question…',
                           'Checking your progress and data sources…',
                           'Synthesizing indexed data…',
                           'Crafting your answer…'
                         ];
      loaderMessages.forEach((loaderMessage)=>{
        this.messages.push(this.i18n.t(loaderMessage));
      });
  }

  addClickEventsToPosts() {
    // Find all elements with the class 'post-link'
    const postLinks = this.el?.nativeElement?.querySelectorAll('.post-link');
    
    // Remove all existing event listeners
    this.post_event_listeners.forEach((listener) => {
      listener();
    });

    postLinks?.forEach((link) => {
      //Removed text-decoration: underline; as it is looking odd 
      link.setAttribute("style", "color: #007bff;cursor: pointer;");
      // Get the post ID from the class name
      const postId = link.querySelector("span").className;

      // Add a click event listener to each link
      let listener = this.renderer.listen(link, "click", () => {
        this.openFullScreenRoutine(parseInt(postId, 10));
      });
      this.post_event_listeners.push(listener);
    });
  }
  docLink(link) {
    return this.sanitizer.bypassSecurityTrustResourceUrl(link)
  }
  trackByFn(index: number, item: any): any {
    return item.id || index;
  }
  ngOnInit() {
    // #2 — Subscribe to in-step action state so Copilot window stays in sync
    this.microActionStateService.actionState$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(state => {
        // Once chips are anchored to a specific action (from chat history), reject updates
        // that belong to a different micro-action screen the user opened elsewhere in the app.
        // A falsy screenContentId on the current state means last-resort mode — accept all
        // updates so the chatbot can provide the full state when it opens.
        if (state && this.microActionState?.screenContentId &&
            String(state.screenContentId) !== String(this.microActionState.screenContentId)) {
          return;
        }
        if (state && (!this.microActionState || state.sessionId !== this.microActionState.sessionId)) {
          this.fooziRecommitAccepted = false;
          this.fooziRecommitDeclined = false;
        }
        this.microActionState = state;
        console.log("microActionState updated:", state);
        this.microActionDescHtml = state?.currentAction?.description
          ? this.sanitizer.bypassSecurityTrustHtml(state.currentAction.description)
          : null;
      });

    this.listenToKeyboardEvents();
    this.addInputEventListeners();
    // Says a panel is on screen, so nothing opens a second one over this one — see hasPanelOnScreen.
    this.fooziAiConciergeService.panelMounted();
    // A micro-action the learner asked to open from another cycle, before the switch reloaded the
    // app around them.
    // Both entry points, because only one of them fires per layout: a panel created by the switch
    // (mobile, where the panel is a modal) finds the request here, and one that was already mounted
    // (web, where it lives beside the home screen) is told about it instead.
    this.fooziAiConciergeService.openPendingAction$.pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.openActionAfterCycleSwitch());
    this.openActionAfterCycleSwitch();
    // BEFORE the branching below, all of which can return. Registered after it, the watch was never
    // created on three of the four paths, so ngDoCheck went on announcing expiry to nobody and the
    // session was never refreshed.
    this.watchSessionValidity();

    // First Coach open of this app run: the learner asked for a blank conversation rather than last
    // night's. Within the same run, re-opening the panel falls through to the resume path below so
    // what they were saying is still there.
    const mode = sessionStorage.getItem(PANEL_MODE_KEY) as PanelMode | null;
    const storedSession = sessionStorage.getItem('foozi_session');

    if (!mode) {
      this.isSessionInitializing = false;
      // Nothing remembered: a newly opened tab, or the first Coach open after logging in. A blank
      // conversation, but with the greeting and its questions — unlike the header's new-chat
      // button, which clears those too.
      this.resetPanel({ greeting: true });
      return;
    }

    if (mode !== 'session') {
      this.isSessionInitializing = false;
      // Refreshed, or came back after switching paths, while the panel had no conversation. Put
      // the same screen back rather than reaching for the server's last one.
      this.resetPanel({ greeting: mode === 'fresh' });
      return;
    }

    if (storedSession) {
      // A conversation was live before the refresh: reopen THAT one, not whichever the server
      // considers most recent — another tab may have moved on since.
      this.isSessionInitializing = false;
      this.loadSessionIntoPanel(storedSession);
      // Deliberately here and not inside loadSessionIntoPanel: that also opens a conversation
      // chosen from History, which is not the active one, and this sync appends the ACTIVE
      // session's new messages to whatever is on screen.
      this.setupPageLeaveSync();
      return;
    }

    // Always use checkResumeSessionValidity as the single source of truth:
    // it returns the active session_id + its messages in one call.
    // session_id present + has messages → retain it and restore history.
    // session_id present but no messages, or no session at all → create fresh.
    const createNewSession = (reason: string) => {
      sessionStorage.removeItem('ma_state');
      this.isSessionRefreshing = true;
      // The screen this learner is on goes onto the session as it is opened - the one moment it can
      // be recorded, there being no endpoint that changes it afterwards.
      const wanted = this.greetingWanted();
      this.fooziAiConciergeService.createChatSession(undefined, wanted)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((newRes) => {
          this.isSessionRefreshing = false;
          this.isSessionInitializing = false;
          this.serverMessageSession = newRes.session_id;
          this.setupPageLeaveSync();
          this.fooziAiConciergeService.setsessionValidityStatus(false);
          sessionStorage.setItem('foozi_session', newRes.session_id);
          sessionStorage.setItem('foozi_session_created', Date.now().toString());
          mixpanel.track('Foozi AI: New session created', {
            session_id: newRes.session_id,
            reason,
          });
          // Read back off the response rather than assumed from `wanted`: the session is the
          // record now, and this is the same value every later open of it will get.
          this.showWelcome = this.greetingFor(newRes?.session_id, newRes, 0, { fallback: wanted });
          this.rememberGreetingForSession(newRes?.session_id, this.showWelcome);
          this.offerSuggestions(newRes?.suggested_questions);
        });
      this.isLoader = true;
      setTimeout(() => {
        const messages = document.querySelectorAll(".info-text");
        this.displayMessages(messages);
        this.isLoader = false;
      }, 1000);
    };

    // checkResumeSessionValidity returns both session_id and messages in one call.
    // session_id + messages → retain session and restore history; otherwise create fresh.
    this.fooziAiConciergeService.checkResumeSessionValidity()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (res) => {
          const serverSessionId = res?.session_id;
          const hasMessages = (res?.messages || []).length > 0;
          // Settled before either branch below: offerSuggestions reads showWelcome.
          this.showWelcome = this.greetingFor(
            serverSessionId, res, (res?.messages || []).length,
            { runScreen: this.greetingRemembered(), fallback: this.greetingWanted() },
          );
          if (serverSessionId) { this.rememberGreetingForSession(serverSessionId, this.showWelcome); }
          if (!serverSessionId) {
            mixpanel.track('Foozi AI: No active session found, creating new session', {session_id: serverSessionId});
            createNewSession('no_active_session');
            return;
          }
          if (!hasMessages) {
            // Session exists but has no messages — reuse it directly.
            // No need to create a new session; the conversation starts empty either way.
            this.isSessionInitializing = false;
            sessionStorage.setItem('foozi_session', serverSessionId);
            sessionStorage.setItem('foozi_session_created', Date.now().toString());
            mixpanel.track('Foozi AI: Active session resumed', {
              session_id: serverSessionId,
              messages_count: 0,
            });
            this.offerSuggestions(res?.suggested_questions);
            const infoMessages = document.querySelectorAll(".info-text");
            this.displayMessages(infoMessages);
            this.serverMessageSession = serverSessionId;
            // Same as adopting or opening one: anything watching which conversation is live — the
            // History list marks it — has to hear about a resumed one as well.
            this.fooziAiConciergeService.setActiveSession(serverSessionId);
            this.setupPageLeaveSync();
            return;
          }
          // Active session with message history — retain it and restore chat.
          this.isSessionInitializing = false;
          sessionStorage.setItem('foozi_session', serverSessionId);
          sessionStorage.setItem('foozi_session_created', Date.now().toString());
          mixpanel.track('Foozi AI: Active session resumed', {
            session_id: serverSessionId,
            messages_count: (res.messages || []).length,
          });
          const infoMessages = document.querySelectorAll(".info-text");
          this.displayMessagesImmediately(infoMessages);
          this.casulaDocumentsList = [];
          for (let message of (res.messages || [])) {
            const row = this.buildChatRow(message);
            if (!row) continue;
            this.chat.push(row);
            setTimeout(() => { this.addClickEventsToPosts(); }, 0);
          }
          // Once, over the whole transcript. This used to hang off the LAST message: the fetch ran
          // only if the final message happened to cite something, so a conversation that ended on a
          // plain answer — most of them — left every attachment above it unloaded behind its
          // skeleton. The index it compared against counted built rows while the length counted
          // stored messages, so one skipped message could miss the end of the loop entirely.
          this.attachCitedDocuments(res.messages);
          this.offerSuggestions(res?.suggested_questions);
          this.serverMessageCount = (res.messages || []).length;
          this.serverMessageSession = serverSessionId;
          this.fooziAiConciergeService.setActiveSession(serverSessionId);
          this.tryRestoreMicroActionState();
          setTimeout(() => { this.scrollToLastMessage(); }, 400);
          this.enableActionsOnLatestAnswer(this.chat);
          this.setupPageLeaveSync();
        },
        () => {
          // Network error — fall back to local session to avoid wiping history.
          this.isSessionInitializing = false;
          const localSession = sessionStorage.getItem('foozi_session');
          this.serverMessageSession = localSession || '';
          this.setupPageLeaveSync();
          if (localSession) {
            const infoMessages = document.querySelectorAll(".info-text");
            this.displayMessages(infoMessages);
          } else {
            createNewSession('network_error');
          }
        }
      );

  }

  /**
   * Replaces the session once it is close to the server's one-hour TTL, driven by ngDoCheck.
   *
   * Its own method so it can be registered before ngOnInit branches: every one of those branches
   * returns, and this used to sit past all of them.
   */
  private watchSessionValidity() {
    this.fooziAiConciergeService.sessionValidityStatus.pipe(takeUntil(this.destroyed$)).subscribe((res) => {
      // Skip if init is still running (avoids racing with checkResumeSessionValidity in ngOnInit)
      // or if status is falsy / a refresh is already in flight.
      // this.oldLogicofsessionClear(res);

      if (this.isSessionInitializing || !res || this.isSessionRefreshing) return;
      this.isSessionRefreshing = true;
      // Always check server state first — the server may still have an active session even if
      // local sessionStorage was cleared (e.g., by the user or a tab reload).
      this.fooziAiConciergeService.checkResumeSessionValidity()
        .pipe(takeUntil(this.destroyed$))
        .subscribe((checkRes) => {
          const serverSessionId = checkRes?.session_id;
          if (serverSessionId) {
            this.fooziAiConciergeService.setsessionValidityStatus(false);
            this.isSessionRefreshing = false;
            mixpanel.track("New session id was set after expiry in same active tab", { session_id: serverSessionId });
            sessionStorage.setItem('foozi_session', serverSessionId);
            sessionStorage.setItem('foozi_session_created', Date.now().toString());
            this.pointPanelAtRefreshedSession(serverSessionId);
          } else {
            // No active session on the server — create a fresh one.
            // Clear in-step state so it cannot target the expired session.
            this.microActionStateService.clear();
            sessionStorage.removeItem('ma_state');
            // Carrying the screen the learner is on. They did not ask for this session — it replaces
            // one that expired under them — so it must not arrive with a greeting they had removed.
            this.fooziAiConciergeService.createChatSession(undefined, this.greetingWanted())
              .pipe(takeUntil(this.destroyed$))
              .subscribe((newSessionRes) => {
                this.fooziAiConciergeService.setsessionValidityStatus(false);
                this.isSessionRefreshing = false;
                mixpanel.track("New session id was set after expiry in same active tab", {});
                sessionStorage.setItem('foozi_session', newSessionRes.session_id);
                sessionStorage.setItem('foozi_session_created', Date.now().toString());
                this.pointPanelAtRefreshedSession(newSessionRes?.session_id);
              }, () => {
                this.isSessionRefreshing = false;
              });
          }
        }, () => {
          this.isSessionRefreshing = false;
        });
    })
  }

  /**
   * Sends the panel's next message to the session that is actually alive.
   *
   * Only where the panel is already holding a conversation: it reads serverMessageSession first, so
   * without this the refresh reached sessionStorage and the message still went to the expired id. A
   * blank panel is left as it is — an id put there while the learner is looking at a fresh screen is
   * exactly what wantsNewConversation exists to ignore.
   */
  private pointPanelAtRefreshedSession(sessionId: string) {
    if (!sessionId || !this.serverMessageSession) { return; }
    this.serverMessageSession = sessionId;
  }

  oldLogicofsessionClear(res: number) {
      let session;
      if (res == 2) {
        // Near-expiry: use checkResumeSessionValidity to refresh the active session id.
        session = this.fooziAiConciergeService.checkResumeSessionValidity();
      } else if (res == 1) {
        session = this.fooziAiConciergeService.createChatSession(undefined, this.greetingWanted());
      }
      if (res && !this.isSessionRefreshing && session) {
        this.isSessionRefreshing = true;
        session.pipe(takeUntil(this.destroyed$)).subscribe((sessionRes) => {
          this.fooziAiConciergeService.setsessionValidityStatus(false);
          this.isSessionRefreshing = false;
          mixpanel.track("New session id was set after expiry in same active tab", {});
          sessionStorage.setItem('foozi_session', sessionRes.session_id);
          sessionStorage.setItem('foozi_session_created', Date.now().toString());
        }, () => {
          this.isSessionRefreshing = false;
        });
      }
  }

  private setupPageLeaveSync() {
    if (this.pageLeaveSetup) return;
    this.pageLeaveSetup = true;
    this.microActionStateService.onPageLeave$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        if (this.isSessionRefreshing) return;

        const container = this.foozicardContainer?.nativeElement as HTMLElement | undefined;
        const savedScrollTop = container?.scrollTop ?? 0;
        const wasAtBottom = container
          ? container.scrollTop + container.clientHeight >= container.scrollHeight - 80
          : true;

        this.isSessionRefreshing = true;
        this.fooziAiConciergeService.checkResumeSessionValidity()
          .pipe(takeUntil(this.destroyed$))
          .subscribe(
            (res) => {
              this.isSessionRefreshing = false;
              if (res?.session_id) {
                sessionStorage.setItem('foozi_session', res.session_id);
                sessionStorage.setItem('foozi_session_created', Date.now().toString());
              }
              const allMessages: any[] = res?.messages || [];
              const incomingSession = res?.session_id || '';
              // Only reset the cursor when a *known* prior session has changed (session rotation).
              // Guarding on this.serverMessageSession prevents a false reset on the very first
              // page-leave sync (where serverMessageSession is still empty), which would otherwise
              // treat all historical messages as new and duplicate them in the chat.
              if (this.serverMessageSession && incomingSession && this.serverMessageSession !== incomingSession) {
                this.serverMessageCount = 0;
              } else if (allMessages.length < this.serverMessageCount) {
                this.serverMessageCount = 0;
              }
              if (incomingSession) {
                this.serverMessageSession = incomingSession;
              }
              const newMessages = allMessages.slice(this.serverMessageCount);
              if (!newMessages.length) return;
              for (const message of newMessages) {
                const isAssistant = message.role === 'assistant' || message.role === 'coach';
                const isUser = message.role === 'user';
                const role = isAssistant ? 'foozi-response' : isUser ? 'user' : 'system';
                const classValue = isAssistant
                  ? 'foozi-responz-wrap'
                  : isUser
                    ? 'justify-content-end user-input-wraper'
                    : 'info-event-wrap';
                const contentStr = typeof message.content === 'string' ? message.content : '';
                const actionData = message?.action || null;
                if (!contentStr.trim() && !actionData) continue;
                const modifiedMessage = contentStr
                  .replace(
                    /\[post_id-(\d+)-([\w\s.]+)-?\]/g,
                    (_m: any, postId: any, name: any) =>
                      ` (<span class="post-link"><span class=${postId}>See ${name}'s post</span></span>)`,
                  )
                  ?.replace(
                    /\s*(?:\[path_id-\d+-step-\d+\]|\[s*path.*?step-\d+]|\[s*path<\/em>id-\d+-step-\d+])\s*/g,
                    '',
                  );
                this.chat.push({
                  role,
                  message: modifiedMessage,
                  actionTitle: role === 'foozi-response' ? (actionData?.title || null) : null,
                  actionDescRaw: role === 'foozi-response' ? (actionData?.description || null) : null,
                  actionDescHtml: role === 'foozi-response' && actionData?.description
                    ? this.sanitizer.bypassSecurityTrustHtml(actionData.description) : null,
                  screenContentId: role === 'foozi-response' && actionData
                    ? (message?.screen_content_id || null) : null,
                  eventType: message.type || null,
                  class: classValue,
                  // noop true, then the newest answer alone is given its bar below — appending
                  // three messages must not put three bars on screen.
                  noop: true,
                });
              }
              this.serverMessageCount = allMessages.length;
              this.enableActionsOnLatestAnswer(this.chat);
              // Detect if the action context changed (user operated on a different step's action).
              // When context changes: re-establish chip state and use an instant scroll to bottom
              // instead of smooth — avoids the "jump to top then animate to bottom" caused by the
              // layout shift that occurs when the prior action's chips collapse on chatbot close.
              const newLastAction = [...this.chat].reverse()
                .find((m: any) => m.role === 'foozi-response' && (m.actionTitle || m.actionDescHtml));
              const contextChanged = !!newLastAction && (
                !this.microActionState ||
                (newLastAction.screenContentId &&
                  String(newLastAction.screenContentId) !== String(this.microActionState?.screenContentId))
              );
              if (contextChanged) {
                this.microActionState = null;
                this.tryRestoreMicroActionState();
              }
              setTimeout(() => {
                if (contextChanged && container) {
                  container.scrollTo({ top: container.scrollHeight, behavior: 'instant' as ScrollBehavior });
                } else if (wasAtBottom) {
                  this.scrollToLastMessage1();
                } else if (container) {
                  container.scrollTo({ top: savedScrollTop, behavior: 'instant' as ScrollBehavior });
                }
              }, 50);
            },
            () => { this.isSessionRefreshing = false; },
          );
      });
  }

  private findHabitByScreenContentId(screenContentId: string | number): any | null {
    const stepsData: any[] = this.appState.get('DailyStepsData') || [];
    const scid = +screenContentId;
    if (!scid) return null;
    for (const chapter of stepsData) {
      for (const step of (chapter.steps || [])) {
        for (const screen of (step.screens || [])) {
          if (screen?.screenContents?.id === scid) {
            return {
              screenContents: screen.screenContents,
              stepId: step?.step?.id || step?.stepId || step?.id || '',
              screenId: screen?.id || '',
              stepTitle: step?.title || step?.step?.title || '',
            };
          }
        }
      }
    }
    return null;
  }

  private tryRestoreMicroActionState(): void {
    if (this.microActionState) return;

    // Only restore if the MOST RECENT foozi-response carries action data.
    // If the newest response is a general query answer (no action), the user
    // has moved on — don't re-anchor chips to an older completed action.
    let lastActionMsg: any = null;
    for (let i = this.chat.length - 1; i >= 0; i--) {
      const m = this.chat[i];
      if (m.role === 'foozi-response') {
        if (m.actionTitle || m.actionDescHtml) {
          lastActionMsg = m;
        }
        break; // stop at the newest foozi-response regardless of type
      }
    }
    if (!lastActionMsg) return;

    // Load ma_state. Validated against the ACTION it belongs to, never against the conversation on
    // screen: a customisation thread is a separate stream with its own id, so comparing the two
    // would reject every stored state and fall through to the last resort below.
    let storedState: any = null;
    try {
      const raw = sessionStorage.getItem('ma_state');
      if (raw) {
        // Kept whatever it carries. It is validated below against the action the latest card names,
        // which is the check that matters; requiring an id here discarded states written before one
        // was stored, and with them the chips they would have restored.
        storedState = JSON.parse(raw) || null;
      }
    } catch {}

    // Derive isAccepted from the LAST action-related event in the restored chat.
    // Since the intermediate "Use This / Refine Again" step was removed (commit 71456a383),
    // action_customize events are now auto-accepted — no longer a "pending" state.
    //   last event = action_customization_saved → isAccepted = true (legacy sessions)
    //   last event = action_customize           → isAccepted = true (auto-accepted, new flow)
    //   nothing matched                         → fall back to live snapshot / ma_state / true
    const lastStatusEvent = [...this.chat]
      .reverse()
      .find((m: any) =>
        m.eventType === 'action_customization_saved' ||
        (m.eventType === 'action_customize' && (m.actionTitle || m.actionDescHtml))
      );
    const serverDerivedIsAccepted: boolean | undefined =
      lastStatusEvent !== undefined ? true : undefined;

    // Live snapshot — second priority after server-derived value
    // Only a snapshot for the SAME action can say whether this card was accepted. Taking any
    // snapshot's value lets a stale one from another action hide the chips entirely.
    const liveSnapshot = this.microActionStateService.snapshot;
    const cardScreenContentId = lastActionMsg.screenContentId;
    const liveIsAccepted = (liveSnapshot && cardScreenContentId
      && String(liveSnapshot.screenContentId) === String(cardScreenContentId))
      ? liveSnapshot.isAccepted
      : undefined;

    // Resolve screenContentId: prefer live message field, fall back to ma_state
    const screenContentId = lastActionMsg.screenContentId
      || storedState?.screenContentId
      || storedState?.scid;

    // Live lookup from DailyStepsData
    if (screenContentId) {
      const found = this.findHabitByScreenContentId(screenContentId);
      if (found) {
        const sc = found.screenContents;
        // Empty rather than the chat session: the customisation thread is resolved by action, on
        // demand, the first time a customise call needs it (see actionSessionFor).
        const sessionId = storedState?.sessionId || '';
        this.microActionStateService.update({
          sessionId,
          scid: String(screenContentId),
          stepId: found.stepId || storedState?.stepId || '',
          screenId: found.screenId || storedState?.screenId || '',
          screenContentId,
          currentAction: {
            title: lastActionMsg.actionTitle || storedState?.currentAction?.title || '',
            description: lastActionMsg.actionDescRaw || storedState?.currentAction?.description || '',
          },
          stepTitle: found.stepTitle || storedState?.stepTitle || '',
          engaged: storedState?.engaged ?? false,
          canSchedule: !!sc?.content?.canSchedule,
          hasToolLink: !!sc?.content?.toolLink,
          toolLink: sc?.content?.toolLink || '',
          tool: sc?.tool || storedState?.tool || null,
          canCompleteNow: !!sc?.content?.canCompleteNow,
          isActionCompleted: !!sc?.userResponse?.userActionCompletion,
          isAccepted: serverDerivedIsAccepted ?? liveIsAccepted ?? storedState?.isAccepted ?? true,
        });
        return;
      }
    }

    // Fall back to ma_state if the live lookup found nothing.
    if (storedState) {
      // Validate action identity: the panel can show cards for more than one action. Skip the
      // restore if the stored title diverges from the latest card's, which would leave the chips
      // targeting an older action.
      const storedTitle = storedState?.currentAction?.title || '';
      const lastTitle = lastActionMsg.actionTitle || '';
      if (!storedTitle || !lastTitle || storedTitle === lastTitle) {
        this.microActionStateService.update(storedState);
        return;
      }
      // Title mismatch: storedState targets a different (older) action — fall through.
    }

    // Last resort: the server returned an action card but screenContentId is missing
    // (server doesn't always propagate scid to stored messages) and sessionStorage was
    // cleared (fresh login). Set minimal state so chips are visible. canSchedule and
    // canCompleteNow will be corrected when the micro-action chatbot opens via actionState$.
    if (lastActionMsg.actionTitle) {
      this.microActionStateService.update({
        sessionId: '',
        scid: '',
        stepId: '',
        screenId: '',
        screenContentId: '',
        currentAction: {
          title: lastActionMsg.actionTitle || '',
          description: lastActionMsg.actionDescRaw || '',
        },
        stepTitle: '',
        engaged: false,
        canSchedule: true,
        hasToolLink: false,
        toolLink: '',
        canCompleteNow: false,
        isActionCompleted: false,
        isAccepted: serverDerivedIsAccepted ?? liveIsAccepted ?? true,
      });
    }
  }

  isCurrentActionMsg(chatmsg: any): boolean {
    if (!this.microActionState) return false;
    for (let i = this.chat.length - 1; i >= 0; i--) {
      const m = this.chat[i];
      if (m.role === 'foozi-response' && (m.actionTitle || m.actionDescHtml)) {
        return m === chatmsg;
      }
    }
    return false;
  }

  useThisAction(chatmsg: any) {
    const state = this.microActionState;
    if (!state) return;

    const accepted = {
      title: state.currentAction.title,
      description: state.currentAction.description || '',
    };

    // Optimistic UI update — push bubbles and update state immediately
    this.chat.push({
      role: 'user',
      message: this.i18n.t('Use this action'),
      class: 'justify-content-end user-input-wraper',
    });
    const updatedState = { ...state, isAccepted: true };
    this.microActionStateService.update(updatedState);
    sessionStorage.setItem('ma_state', JSON.stringify(updatedState));
    // Carry actionTitle + actionDescHtml on the confirmation bubble so that
    // isCurrentActionMsg() targets THIS bubble (bottom of chat), not the original
    // action card at the top. Without these fields the scan falls back to the
    // first foozi-response that has actionTitle, making chips jump to the top card.
    this.chat.push({
      role: 'foozi-response',
      message: 'Done — I have updated this action',
      class: 'foozi-responz-wrap',
      noop: true,
      actionTitle: accepted.title,
      actionDescRaw: accepted.description,
      actionDescHtml: this.sanitizer.bypassSecurityTrustHtml(accepted.description),
      screenContentId: chatmsg.screenContentId || state.screenContentId || null,
    });
    setTimeout(() => this.scrollToLastMessage(), 100);

    this.acceptMicroActionInline(updatedState, state, accepted);
  }

  // Persists the auto-accepted action to the backend without intermediate UI.
  // originalState is used to revert the optimistic update if persistence fails.
  private acceptMicroActionInline(
    state: MicroActionSessionState,
    originalState: MicroActionSessionState,
    accepted: { title: string; description: string },
  ): void {
    this.fooziAiConciergeService.saveActionCustomization({
      screen_content_id: state.screenContentId,
      step_id: state.stepId,
      screen_id: state.screenId,
      user_action_completion: state.isActionCompleted,
      title: accepted.title,
      description: accepted.description,
      base_title: state.stepTitle,
    }).pipe(
      takeUntil(this.destroyed$),
      catchError(() => {
        this.microActionStateService.update(originalState);
        sessionStorage.setItem('ma_state', JSON.stringify(originalState));
        this.toaster.error(this.i18n.t('Failed to save action. Please try again.'));
        return of(null);
      }),
    ).subscribe();
  }

  get isCompletedInTodoList(): boolean { return !!this.findMatchingTodoAction()?.userActionCompletion; }

  // True when completed per microActionState (init-time snapshot) OR per live todoActions.
  // todoActions is authoritative after a to-do-list completion that hasn't propagated back to state.
  get isActionCompleted(): boolean {
    return !!this.microActionState?.isActionCompleted || this.isCompletedInTodoList;
  }

  private findMatchingTodoAction(): any | null {
    const screenContentId = this.microActionState?.screenContentId;
    if (!screenContentId) return null;
    const todoActions: any[] = this.appState.get('todoActions') || [];
    const matches = todoActions.filter(a => String(a.screenContentId) === String(screenContentId));
    if (!matches.length) return null;
    return matches.reduce((best, a) => (Number(a.id) > Number(best.id) ? a : best), matches[0]);
  }

  private formatActionCompletionDate(action: any): string {
    const iso = action?.actionCompletionDateTime;
    if (!iso) return '';
    try {
      // The learner's language, not the browser's — see I18n.locale.
      return new Date(iso).toLocaleString(this.i18n.locale, {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
      });
    } catch { return ''; }
  }

  private buildCalendarParams(title: string, notes: string): any {
    const start = new Date();
    const end = new Date(start.getTime() + 10 * 60 * 1000);
    return {
      startDate: start,
      endDate: end,
      title: title || '',
      eventLocation: '',
      notes: notes || '',
      endEventTime: null,
      frequency: null,
    };
  }

  private async openAndScheduleCalendar(state: MicroActionSessionState): Promise<void> {
    const calendarParams = this.buildCalendarParams(
      state.currentAction.title,
      state.currentAction.description,
    );
    this.homeV5Service.setCalendarData(calendarParams);

    if (this.hasNativeCalendar) {
      this.appState.set('scheduledHabitId', state.screenContentId);
      this.createFooziCalendarEvent(calendarParams);
    } else {
      const modal = await this.modalController.create({
        component: AlertWebactionCalendarComponent,
        cssClass: this.isMobile ? 'custom-modal-size' : 'web-action-calender-modal',
        animated: false,
      });
      await modal.present();
      modal.onDidDismiss().then(() => {
        this.toaster.success(this.i18n.t('Micro-action added to To-Do List'));
      });
    }

  }

  private createFooziCalendarEvent(calendarParams: any): void {
    const success = (message: any) => console.log('Success: ' + JSON.stringify(message));
    const error = (message: any) => console.error('Error: ' + message);
    const w: any = window;
    const calOptions = w.plugins.calendar.getCalendarOptions();
    const currentPath = this.appState.get('currentPath');
    const daysCount = (currentPath?.lastDayOfContent?.() ?? 60) * 1.5;
    const recurrenceEnd = new Date();
    recurrenceEnd.setDate(recurrenceEnd.getDate() + Math.ceil(daysCount));
    calOptions.recurrenceInterval = 1;
    calOptions.firstReminderMinutes = 0;
    calOptions.recurrenceEndDate = recurrenceEnd;
    this.calendarService.scheduleEvents(
      calendarParams.title,
      calendarParams.eventLocation,
      calendarParams.notes,
      calendarParams.startDate,
      calendarParams.endDate,
      calOptions,
      success,
      error,
    );
  }

  private saveCommitAction(state: MicroActionSessionState, echoToChat = true): void {
    let matchedAction =this.findMatchingTodoAction()
    const commitTitle = state.currentAction?.title || '';
    const commitTitleHtml = commitTitle ? `<span class="system-action-highlight">${commitTitle}</span>` : '';
    const commitMsg = commitTitle
      ? `Action: ${commitTitleHtml} committed to your schedule`
      : 'Action committed to your schedule';
    this.kyperApiService.schedule_commit_action_kyper({
      step_id: state.stepId,
      screen_id: state.screenId,
      screen_content_id: state.screenContentId,
      user_action_scheduled_later: true,
      // customization_id: matchedAction?.customizationId,
      action_date_time: this.kyperApiService.formatDate(),
    }).pipe(takeUntil(this.destroyed$)).subscribe({
      next: (res: any) => {
        if (echoToChat) {
          this.chat.push({ role: 'system', message: commitMsg, class: 'info-event-wrap' });
        }
        setTimeout(() => this.scrollToLastMessage1(), 100);
        this.toaster.success(this.i18n.t('Micro-action added to To-Do List'));
        const updatedState = { ...state, isAccepted: res?.data?.userActionScheduledLater ?? state.isAccepted };
        this.microActionStateService.update(updatedState);
        sessionStorage.removeItem('ma_state');
        const pathId = this.path?.id;
        const appendEvent = () => {
          // Only when the learner is acting in THIS panel. Acting in the micro-action screen — the
          // in-step one, or the same screen opened from History — that screen records the event on
          // the action's thread itself (appendSystemEventToServer), and writing it here as well put
          // the identical system turn on the thread twice, one POST from each component.
          if (!echoToChat) { return; }
          // No serverMessageCount bump: this event is stored on the ACTION's own session, so the
          // chat session did not grow. Counting it there pushed the page-leave cursor past the real
          // length, and the sync's own overshoot guard then reset it to 0 and re-appended the whole
          // transcript — every message twice.
          this.appendActionEvent(state, {
            role: 'system',
            type: 'action_committed',
            content: commitMsg,
            screen_content_id: state.scid,
          }).pipe(takeUntil(this.destroyed$)).subscribe();
        };
        if (pathId) {
          this.kyperApiService.getActionsList({ path_id: pathId })
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
              next: (data) => { this.appState.set('todoActions', data?.actions); appendEvent(); },
              error: () => appendEvent(),
            });
        } else {
          appendEvent();
        }
      },
      error: () => {
        this.toaster.error(this.i18n.t('Failed to commit action. Please try again.'));
      },
    });
  }

  showFooziRecommitToast(action: 'commit' | 'complete') {
    const matched = this.findMatchingTodoAction();
    // Allow directly only when not completed, OR when live todo confirms a fresh uncompleted action
    // (recommit flow already reset it). Block when state says completed and todo isn't hydrated yet.
    if (!this.isActionCompleted || (matched && !matched.userActionCompletion)) {
      if (action === 'commit') this.finalizeInStepAction();
      else this.markCompletedAction();
      return;
    }
    const completedAt = matched ? this.formatActionCompletionDate(matched) : undefined;
    this.toaster.showRecommitConfirm(
      () => this.executeFooziRecommit(action, matched),
      () => {},
      completedAt,
    );
  }

  private executeFooziRecommit(action: 'commit' | 'complete',matchedAction): void {
    this.fooziRecommitAccepted = true;
    this.fooziRecommitDeclined = false;
    const state = this.microActionState;
    const actionId = state?.screenContentId;

    if (!actionId || !state) return;

    const finalProceed = () => {
      if (action === 'commit') {
        this.openAndScheduleCalendar(state).then(() => this.saveCommitAction(state));
      } else {
        this.markCompletedAction();
      }
    };

    this.routineV2Service.repeatUserAction({ action_id: matchedAction?.id })
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          const pathId = this.path?.id;
          // this.kyperApiService.schedule_commit_action_kyper({
          //   step_id: state.stepId,
          //   screen_id: state.screenId,
          //   screen_content_id: state.screenContentId,
          //   user_action_scheduled_later: false,
          //   user_action_completion: false,
          //   customization_id:matchedAction?.customizationId

          //   // action_date_time: this.kyperApiService.formatDate(),
          // }).pipe(takeUntil(this.destroyed$)).subscribe({
          //   next: () => {
              if (pathId) {
                this.kyperApiService.getActionsList({ path_id: pathId })
                  .pipe(takeUntil(this.destroyed$))
                  .subscribe({
                    next: (data) => { this.appState.set('todoActions', data?.actions); finalProceed(); },
                    error: () => finalProceed(),
                  });
              } else {
                finalProceed();
              }
            },
            error: finalProceed,
          });
      //   },
      //   error: finalProceed,
      // });
  }

  // Called when user confirms recommit via the textarea (re-customize path).
  private executeFooziRecommitCustomize(userText: string,matchedAction): void {
    const state = this.microActionState;
    if (!state) return;
    this.fooziRecommitAccepted = true;
    const actionId = state.screenContentId;
    const doCustomize = () => {
      this.isMicroActionRefineMode = true;
      this.postMsg = userText;
      this.sendMsg();
    };

    if (!actionId) {
      doCustomize();
      return;
    }

    this.routineV2Service.repeatUserAction({ action_id: matchedAction?.id })
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          const pathId = this.path?.id;
          // this.kyperApiService.schedule_commit_action_kyper({
          //   step_id: state.stepId,
          //   screen_id: state.screenId,
          //   screen_content_id: state.screenContentId,
          //   user_action_scheduled_later: false,
          //   user_action_completion: false,
          //   customization_id:matchedAction?.customizationId
          //   // action_date_time: this.kyperApiService.formatDate(),
          // }).pipe(takeUntil(this.destroyed$)).subscribe({
          //   next: () => {
          
              if (pathId) {
                this.kyperApiService.getActionsList({ path_id: pathId })
                  .pipe(takeUntil(this.destroyed$))
                  .subscribe({
                    next: (data) => { this.appState.set('todoActions', data?.actions); doCustomize(); },
                    error: () => doCustomize(),
                  });
              } else {
                doCustomize();
              }
            },
            error: doCustomize,
          });
      //   },
      //   error: doCustomize,
      // });
  }

  openFooziToolLink() {
    const tool = this.microActionState?.tool;
    if (tool) {
      this.openDocument(tool);
      return;
    }
    // No tool document — open the URL itself. An action opened from History knows its tool_link
    // but not the document behind it, since that is read from the path and the path holds only the
    // cycle the learner is in now. Without this the chip was simply inert for those actions.
    const link = this.microActionState?.toolLink;
    if (!link) { return; }
    const target = this.platform.is('cordova') ? '_system' : '_blank';
    window.open(link, target);
  }

   toggleTheme() {
    this.homeUiService.toggleTheme();
    this.cdRef.detectChanges(); 
  }

    removeBracketContentFromHtml(content: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    const pTags = doc.querySelectorAll('p');

    pTags.forEach((p) => {
      p.textContent = p.textContent?.replace(/\s*\[path_id-\d+-step-\d+\]\s*/g, '').trim();
    });

    return doc.body.innerHTML;
  }
   getAndroidScrollContainerHeight(): string {
    const wrap = document.querySelector(".ai-text-area-wrap") as HTMLElement;
    const wrapHeight = wrap ? wrap.offsetHeight : 69;
    return `calc(100vh - ${wrapHeight + 194}px)`;
   }
   getIosScrollContainerHeight(): string {
    const wrap = document.querySelector(".ai-text-area-wrap") as HTMLElement;
    const wrapHeight = wrap ? wrap.offsetHeight : 69;
    return `calc(100vh - ${wrapHeight + 213}px)`;
   }
   getWebScrollContainerHeight(): string | null {
    const container = document.querySelector(".web-ai-continer-scroll-y") as HTMLElement;
    const wrap = document.querySelector(".ai-bottom-text-area") as HTMLElement;
    if (!container || !wrap) {
      return null;
    }
    const parent = container.parentElement as HTMLElement;
    const parentTop = parent ? parent.getBoundingClientRect().top : container.getBoundingClientRect().top;
    const wrapTop = wrap.getBoundingClientRect().top;
    return `${wrapTop - parentTop}px`;
   }
   addInputEventListeners() {
    const inputs = document.querySelector("textarea");
    const element = document.querySelector(".mob") as HTMLElement;
    const element2 = document.querySelector(".mob-ai-continer-scroll-y") as HTMLElement
    if (inputs && this.platform.is("mobileweb") && element || element2) {
      this.renderer.listen(inputs, "focus", () => {
        element.style.bottom = "45px";
        element2.style.height = "calc(100vh - 270px)";
      });

      this.renderer.listen(inputs, "blur", () => {
        element.style.bottom = "45px";
        if(this.platform.is('android')) {
          element2.style.height = this.getAndroidScrollContainerHeight();
        }
        if (this.platform.is('ios')) {
           element2.style.height = this.getIosScrollContainerHeight();
        }
      });
      this.renderer.listen(inputs, "focusout", () => {
        element.style.bottom = "45px";
          if(this.platform.is('android')) {
          element2.style.height = this.getAndroidScrollContainerHeight();
        }
        if (this.platform.is('ios')) {
           element2.style.height = this.getIosScrollContainerHeight();
        }
      });
    }

   }
  
  checkIsSessionExpired() {
    const session_id = sessionStorage.getItem('foozi_session');
    const createdStr = sessionStorage.getItem('foozi_session_created');
    // Nothing to expire. A blank panel holds no session by design — ensureSession mints one on the
    // first message — and reporting expiry here would have the watch above resolve a session behind
    // a screen the learner asked to be empty.
    if (!session_id) { return 0; }
    // An id with no stamp: its age is unknowable, so treat it as due for replacement.
    if (!createdStr) { return 1; }

    const ageMs = Date.now() - parseInt(createdStr, 10);
    const MAX_AGE_MS = 55 * 60 * 1000;  // 55 min — just under Redis 1hr TTL
    const WARN_AGE_MS = 52 * 60 * 1000; // 52 min — proactively refresh 3 min early

    if (ageMs > MAX_AGE_MS) return 1;
    if (ageMs > WARN_AGE_MS) return 2;
    return 0;
  }
  
  /**
   * Whether the top of the panel introduces the Coach ("I'm Kyper Coach…") rather than asking
   * "What should we explore next?".
   *
   * The introduction is for a first meeting only: a greeted panel the Coach has not answered in yet.
   * Once a reply has arrived, after the header's new-chat button, and on a conversation reopened from
   * History the learner already knows who they are talking to. Read off the transcript, so a refresh
   * or a cycle switch that puts the conversation back reaches the same answer.
   */
  get isFirstGreeting(): boolean {
    return this.showWelcome
      && !this.openedFromHistory
      && !this.chat.some((row: any) => row?.role === 'foozi-response');
  }

  ngDoCheck(): void {
    if (!this.isSessionInitializing) {
      if (this.checkIsSessionExpired()==2 && !this.isSessionRefreshing) {
        this.fooziAiConciergeService.setsessionValidityStatus(2);
      } else if (this.checkIsSessionExpired()==1 && !this.isSessionRefreshing) {
        this.fooziAiConciergeService.setsessionValidityStatus(1);
      }
    }
    if (this.appState.get('isDocumentOpenedFromFoozi') == true) {
      this.appState.set('isDocumentOpenedFromFoozi', undefined);
      let ids= this.casulaDocumentsList.map((doc)=>doc.id)
      let id =this.appState.get('singleDocument')?.id
      this.openFullScreenCasualDocuments(id)
     // this.fetchCasualDocuments(id)
    }
    this.refreshSuggestionsOnCycleChange();
  }

  /** Asks for the new cycle's chips when the cycle changes under a settled panel. */
  private refreshSuggestionsOnCycleChange(): void {
    if (!this.suggestionsAllowed || this.suggestionsPathId === undefined) { return; }
    const pathId = this.appState.get('currentPath')?.id ?? null;
    // Null mid-switch, while the old cycle is cleared and the new one not yet loaded.
    if (pathId === null || pathId === this.suggestionsPathId) { return; }
    this.loadSuggestedQuestions();
  }
   removeInputEventListeners() {
    const inputs = document.querySelector("textarea");
    if (inputs) {
       this.renderer.listen(inputs, "focus", null);
       this.renderer.listen(inputs, "blur", null);
       this.renderer.listen(inputs, "focusout", null);
    }
   
  }

  listenToKeyboardEvents() {
    this.keyboard.onKeyboardDidShow().subscribe((event: any) => {
      const keyboardHeight = event.keyboardHeight;
      this.isKeyboardVisible = true;
      this.currentKeyboardHeight = keyboardHeight;
      this.adjustForKeyboard(true, keyboardHeight);
      this.scrollChatToBottom();
    });

    this.keyboard.onKeyboardWillHide().subscribe(() => {
      this.isKeyboardVisible = false;
      this.currentKeyboardHeight = 0;
      this.adjustForKeyboard(false, 0);
    });
  }


  adjustForKeyboard(isKeyboardVisible: boolean, keyboardHeight: number ) {
    const element = document.querySelector('.mob') as HTMLElement;
    const element2 = document.querySelector(".mob-ai-continer-scroll-y") as HTMLElement;
    if (element || element2) {
      if (isKeyboardVisible) {
        if (this.platform.is('ios')) {
            element.style.bottom = `${keyboardHeight + 8}px`; // Remove bottom spacing
            element2.style.height = `calc(100vh - ${keyboardHeight}px - 181px )`;
        }
        else if (this.platform.is('android')) {
          element.style.bottom = `${keyboardHeight + 5}px`;
          element2.style.height = `calc(100vh - ${keyboardHeight}px - 190px )`;
        }
      } else {
        if (this.platform.is('ios')) {
          element.style.bottom = "45px";
          element2.style.height ='calc(100vh - 216px)';
        }else if (this.platform.is('android')) {
          element.style.bottom = "45px";
          element2.style.height ='calc(100vh - 230px)';
        }
      }
    }
  }

  ngAfterViewInit(): void {
    this.fooziTextAreaLabel = this.i18n.t("Message Kyper Coach");
    console.log("this.fooziTextAreaLabel", this.fooziTextAreaLabel, this.i18n.t("What impact do you expect—or have you already seen—as a result of this action?"));
    this.cdRef.detectChanges();
  }

  private capitalizeSentences(text: string): string {
    return text.replace(/(?:^|\.\s)([a-z])/g, (match) => match.toUpperCase());
  }
  adjustTextareaHeight(event): void {
    const textareaElement = this.textarea.getInputElement();
    let ele = document.getElementsByClassName("foozi-dynamic-textarea");
    const lineHeight = parseFloat(getComputedStyle(this.textarea['el']).lineHeight);
    if (event && event.target.value) {
      this.postMsg = this.capitalizeSentences(event.target.value);
    }
    textareaElement.then((textarea: HTMLTextAreaElement) => {
      let maxHeight;
      if (this.isMobile) {
        maxHeight = Math.min(window.innerHeight * 0.4, lineHeight * 10);
      }
      else {
        maxHeight = lineHeight * 10;
      }
      const currentScrollTop = textarea.scrollTop;
      textarea.style.height = "auto";
      textarea.style.overflow = "hidden";
      textarea.style.height = textarea.scrollHeight + "px";
      if (textarea.scrollHeight > maxHeight) {
        textarea.style.overflow = "auto";
        textarea.style.height = maxHeight + "px";
      }
      this.rowCount = Math.ceil(textarea.scrollHeight / lineHeight);
      if (this.rowCount > 1 && ele) {
        if (this.isMobile) {
          ele[0].setAttribute("style", "margin-bottom:0px !important;");
        }
        else {
         ele[0].setAttribute("style", "margin-bottom:12px !important;");
        }
      }
      else {
          ele[0].setAttribute("style", "margin-bottom:-20px !important;");
      }
      textarea.offsetHeight; // Trigger reflowg
      if (this.isMobile) {
        if (this.isKeyboardVisible) {
          this.adjustForKeyboard(true, this.currentKeyboardHeight);
        } else {
          const scrollContainer = document.querySelector(".mob-ai-continer-scroll-y") as HTMLElement;
          if (scrollContainer) {
            if (this.platform.is('android')) {
              scrollContainer.style.height = this.getAndroidScrollContainerHeight();
            } else if (this.platform.is('ios')) {
              scrollContainer.style.height = this.getIosScrollContainerHeight();
            }
          }
        }
      } else {
        const webScrollContainer = document.querySelector(".web-ai-continer-scroll-y") as HTMLElement;
        const webHeight = this.getWebScrollContainerHeight();
        if (webScrollContainer && webHeight) {
          webScrollContainer.style.height = webHeight;
        }
      }
      // Check if the user is at the bottom of the textarea
      const isAtBottom =
        textarea.scrollHeight - textarea.scrollTop <=
        textarea.clientHeight + 1;
      if (isAtBottom) {
        // Scroll to the bottom
        textarea.scrollTop = textarea.scrollHeight;
      } else {
        // Restore the previous scroll position if not near the bottom
        textarea.scrollTop = currentScrollTop;
      }
    });
  }

  // scrollToLastMessage() {
  //   this.foozicardContainer.nativeElement.scrollTop =
  //     this.foozicardContainer.nativeElement.scrollHeight;
  // }


  // after asking que to the foozi scroll up
  /**
   * Waits for the transcript to stop growing, then scrolls to the bottom once.
   *
   * The wait is why this exists. Restoring a conversation renders the action card's chips on a
   * LATER change-detection pass (the state goes out through MicroActionStateService and comes back
   * through its subscription), and markdown, images and the greeting settle after that again. Each
   * makes the page taller, so a scroll fired straight away measures a bottom that then moves.
   *
   * Only ONE scroll, and only at the end. Re-issuing a smooth scroll restarts the animation from
   * wherever it had reached, so scrolling on every measuring pass stuttered up the page instead of
   * gliding down it — measuring is a cheap height read and needs no scroll of its own.
   */
  private scrollToBottomWhenSettled(attempts = 6, intervalMs = 120): void {
    const container = this.foozicardContainer?.nativeElement as HTMLElement | undefined;
    if (!container) { return; }

    let lastHeight = -1;
    let left = attempts;
    let cancelled = false;

    // The learner may start dragging while the page is still settling — the wait is longer than
    // the single timed scroll it replaced, so there is more room for that on a phone. Scrolling
    // then fights their finger, and on iOS a programmatic smooth scroll mid-drag lurches. Their
    // touch wins: they have chosen where to look.
    const giveUp = () => { cancelled = true; detach(); };
    const detach = () => {
      container.removeEventListener('touchstart', giveUp);
      container.removeEventListener('wheel', giveUp);
    };
    container.addEventListener('touchstart', giveUp, { passive: true });
    container.addEventListener('wheel', giveUp, { passive: true });

    const settle = () => {
      if (cancelled) { return; }
      const height = container.scrollHeight;
      // Unchanged since the previous read: nothing further is rendering, so this really is bottom.
      if (height === lastHeight || --left <= 0) {
        detach();
        container.scrollTo({ top: height, behavior: 'smooth' });
        return;
      }
      lastHeight = height;
      setTimeout(settle, intervalMs);
    };

    setTimeout(settle, 0);
  }

  scrollToLastMessage1() {
    if (this.foozicardContainer) {
      // console.log('scrollToLastMessage1', Math.random());
      const container = this.foozicardContainer?.nativeElement;
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth', duration:100 });
    }
  }

  // on open foozi last response fixed to the top
  scrollToLastMessage() {
    if (this.foozicardContainer) {
      // console.log('scrollToLastMessage', Math.random());
      const container = this.foozicardContainer.nativeElement as HTMLElement;
      const contentParent = document.getElementById('content');
      if (!contentParent) {
        console.warn('Parent element with id="content" not found');
        return;
      }
      // Find the last element with class 'foozi-response' inside the container
      const messages = container.querySelectorAll('.foozi-response');
      if (messages.length === 0) {
        console.warn('No elements with class "foozi-response" found inside container');
        return;
      }
      const lastMessage = messages[messages.length - 1] as HTMLElement;
      const contentRect = contentParent.getBoundingClientRect();
      const lastMessageRect = lastMessage.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const offsetDifference = lastMessageRect.top - contentRect.top;
      
      const scrollToPosition = container.scrollTop + offsetDifference - 25;
      container.scrollTo({
        top: scrollToPosition,
        behavior: 'smooth'
      });
    }
  }
  
  // while getting response from the foozi scroll to the bottom
  scrollToLastMessage2() {
    // Assuming the chat container has a class 'chat-container'
    const chatContainer = document.querySelector('#scrollBottom');
    if (!chatContainer) return; // Exit if container not found

    // console.log('scrollToLastMessage2', Math.random());
    // Scroll to the bottom smoothly
    chatContainer.scrollTo({
      top: chatContainer.scrollHeight,
      behavior: 'smooth'
    });
  }

  scrollChatToBottom() {
    setTimeout(() => {
      const el = this.scrollBottom?.nativeElement as HTMLElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    }, 0);
  }



  displayMessages(messages) {
    let delay = 0;
    messages.forEach((message, index) => {
      setTimeout(() => {
        message.style.display = "block";
      }, delay);
      delay += 1000; // Adjust delay between messages (in milliseconds)
    });
  }
  displayMessagesImmediately(messages) {
    messages.forEach((message, index) => {
      message.style.display = "block";
    });
  }

  //chat = [
  //   {
  //     role: 'user',
  //     message: 'I heard there was a recent discussion about motivating teams. Can you summarize the main points from that conversation?',
  //     class: 'justify-content-end user-input-wraper'
  //   },
  //   {
  //     role: 'foozi-response',
  //     message: 'Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry\'s standard dummy text ever since the 1500s,',
  //     class: 'foozi-responz-wrap'
  //   },
  //   // more conversations here
  // ];
  changeSubTextFont(index) {
    if (this.fooziDoc) {
      const element = this.fooziDoc.toArray()[index]?.nativeElement;
      const specificElement = element?.querySelector('p') || element?.querySelector('h1') || element?.querySelector('h2');
      if (specificElement) {
        this.renderer.setStyle(specificElement, 'font-size', '14px');
        this.renderer.setStyle(specificElement, 'font-weight', '500');
        this.renderer.setStyle(specificElement, 'font-style', 'normal');
        this.renderer.setStyle(specificElement, 'line-height', '19px');
        this.renderer.setStyle(specificElement, 'font-family', 'Roboto');
        this.renderer.setStyle(specificElement, 'align-items', 'center');
         this.renderer.setStyle(specificElement, 'dispaly', 'flex');
        this.renderer.setStyle(specificElement, 'padding', '0px');
        this.renderer.setStyle(specificElement, 'margin', '0px');
      }
    }
    return 0
  }
  resetTextareaHeight(): void {
    const textareaElement = this.textarea.getInputElement();
    textareaElement.then((textarea: HTMLTextAreaElement) => {
      textarea.style.height = 'auto'; // Reset to the default size
      textarea.style.overflow = 'hidden'; // Hide the scrollbar initially
      // textarea.setAttribute("style", "margin-bottom:-20px");      
    });
  }
  onTextModification(ev) {
    // console.log("called on change", ev.target.value);
    this.adjustTextareaHeight(ev)
  }
  sendMsg(event = null, suggestion = null) {
    if (this.showAnimation) return;
  let user = this.appState.get("currentUser");
  mixpanel.track('Kyper Coach message', {
    message: this.postMsg
  });
  if (this.postMsg.trim() === '' && !suggestion) {
    return;
  }
  this.resetTextareaHeight();
  setTimeout(() => {
    this.scrollToLastMessage1();
  }, 100);
  this.textarea.autofocus = false;
  this.textarea['el'].blur();
  if (this.isMobile && this.keyboard.isVisible) {
    this.keyboard.hide();
  }
  if (this.dislikeFeedback && suggestion == null) {
    this.chat.push({
      role: 'user',
      message: this.postMsg,
      class: 'justify-content-end user-input-wraper'
    });
    let response = this.chat[this.chat.length - 3].message;
    let prompt = this.chat[this.chat.length - 4].message;
    this.fooziAiConciergeService.sendLike(response, prompt, this.postMsg, 'false').subscribe((res) => {
      // console.log(res);
      // setTimeout(() => {
      //   this.scrollToLastMessage();
      // }, 200);
    });
    this.dislikeFeedback = false;
    this.chat.push({
      role: 'foozi-response',
      message: "Thank you for your feedback. I will use it to improve my service.",
      class: 'foozi-responz-wrap',
      noop: true
    });
    this.postMsg = "";
    return;
  }
  if (suggestion) {
    this.postMsg = suggestion;
  }
  this.dislikeFeedback = false;

  // If NOT in refine mode and micro-action state is alive, this is a general query —
  // clear the action context now so the message routes to regular foozi-ai chat.
  // (When isMicroActionRefineMode is true, the recursive call from executeFooziRecommitCustomize
  // is intentionally routing to the action customize API — don't clear in that case.)
  if (this.microActionState && !this.isMicroActionRefineMode) {
    this.microActionStateService.clear();
    this.microActionState = null;
  }

  // Gate: if the micro-action is completed, show recommit before sending.
  // After the clear above, this only fires during the recursive executeFooziRecommitCustomize
  // call (isMicroActionRefineMode=true), never for general queries.
  if (this.isActionCompleted && this.postMsg?.trim()) {
    const _matchedTodoAction = this.findMatchingTodoAction();
    if (!_matchedTodoAction || _matchedTodoAction.userActionCompletion) {
      const pendingMsg = this.postMsg.trim();
      const completedAt = _matchedTodoAction ? this.formatActionCompletionDate(_matchedTodoAction) : undefined;
      this.toaster.showRecommitConfirm(
        () => this.executeFooziRecommitCustomize(pendingMsg, _matchedTodoAction),
        () => { this.postMsg = ''; },
        completedAt,
      );
      return;
    }
  }

  // Micro-action refine mode: route to customizeMicroAction API instead of foozi-ai chat
  if (this.isMicroActionRefineMode && this.microActionState && this.postMsg?.trim()) {
    const state = this.microActionState;
    const userText = this.postMsg.trim();
    this.postMsg = '';
    this.isMicroActionRefineMode = false;
    this.chat.push({ role: 'user', message: userText, class: 'justify-content-end user-input-wraper' });
    this.isLoader = true;
    this.showAnimation = true;
    this.actionSessionFor(state).pipe(
      switchMap((sessionId) => this.fooziAiConciergeService.customizeMicroAction(sessionId, state.scid, {
        prompt: userText,
        preset_key: 'refine_again',
      })),
      takeUntil(this.destroyed$),
    ).subscribe({
      next: (res: any) => {
        this.isLoader = false;
        this.showAnimation = false;
        const msg = (res?.message && typeof res.message === 'object') ? res.message : res;
        const content = msg?.content || '';
        const newAction = msg?.action ? { title: msg.action.title, description: msg.action.description || '' } : null;
        const actionDescHtml = newAction ? this.sanitizer.bypassSecurityTrustHtml(newAction.description) : null;
        // Auto-accept: mark isAccepted true immediately — no "Use This / Refine Again" step.
        const updatedState = {
          ...state,
          isAccepted: true,
          ...(newAction ? { currentAction: newAction } : {}),
        };
        this.microActionStateService.update(updatedState);
        sessionStorage.setItem('ma_state', JSON.stringify(updatedState));
        const responseBubble: any = {
          role: 'foozi-response',
          message: content,
          actionTitle: newAction ? newAction.title : null,
          actionDescRaw: newAction ? newAction.description : null,
          actionDescHtml: actionDescHtml,
          screenContentId: state.screenContentId || null,
          class: 'foozi-responz-wrap last-msg-scroll',
          noop: true,
        };
        this.chat.push(responseBubble);
        // A refine is written to the action's own session (customizeMicroAction above), not to the
        // chat session this cursor counts — see the commit path for what over-counting costs.
        this.scrollToLastMessage();
        if (newAction) {
          this.acceptMicroActionInline(updatedState, state, newAction);
        }
      },
      error: () => {
        this.isLoader = false;
        this.showAnimation = false;
      },
    });
    return;
  }

  this.chat.push({
    role: 'user',
    message: this.postMsg,
    class: 'justify-content-end user-input-wraper'
  });
    this.isLoader = true;
    this.showAnimation = true;
  // if (this.isLoader) {
  //  this.startTypingSequence();
  // }
  let postMsg = this.postMsg;
  this.postMsg = "";
  this.ensureSession().pipe(
    switchMap((sessionId) => this.fooziAiConciergeService.sendMessage(sessionId, postMsg)),
    takeUntil(this.destroyed$),
  ).subscribe((res) => {
    setTimeout(() => {
      this.isLikeSelected = false;
    }, 200);
    if (this.chat.length > 2) {
      this.chat[this.chat.length - 2].noop = true;
    }
    // V2 response: res.message.content (not res.past_messages[last].message)
    const responseMessage = res.message || {};
    let modifiedMessage = (responseMessage.content || '').replace(
      /\[post_id-(\d+)-([\w\s.]+)-?\]/g,
      (match, postId, name) => ` (<span class="post-link"><span class=${postId}>See ${name}'s post</span></span>)`
    )?.replace(/\s*(?:\[path_id-\d+-step-\d+\]|\[s*path.*?step-\d+]|\[s*path<\/em>id-\d+-step-\d+])\s*/g, '');
    let id: number[] = [];
    const responseSources: any[] = responseMessage?.sources || [];
    // New format uses tool_id; old format used doc_id — support both
    if (responseSources.length > 0) {
      responseSources.forEach((source: any) => {
        const docId = source?.tool_id ?? source?.doc_id;
        if (docId) id.push(+docId);
      });
      id = [...new Set(id)];
      // No fetchable doc IDs — no async load will happen, so unlock immediately.
      if (id.length === 0) this.loaded = true;
    }
    // Push empty message to start typing effect
    // setTimeout(() => {
      this.chat.push({
        role: 'foozi-response',
        message: '',
        sources: responseSources.length > 0 ? responseSources : null,
        class: 'foozi-responz-wrap last-msg-scroll',
        noop: true
      });
      const lastChatIndex = this.chat.length - 1;
      this.typeWriterEffect(modifiedMessage, lastChatIndex, 2, id); // 15ms per character
      this.serverMessageCount += 2;
      this.isLoader = false;
      this.showAnimation = false;
      
      //this.scrollToLastMessage();
      // }, 14000);
    // if (id && id.length > 0) {
    //   this.fetchCasualDocuments(id);
    // }
 
    // this.isLoader = false;
  }, (error) => {
    this.isLoader = false;
    this.showAnimation = false;
    const displayMsg = this.sendErrorMessage(error);
    this.chat.push({
      role: 'foozi-response',
      message: displayMsg,
      class: 'foozi-responz-wrap',
      noop: true,
    });
    this.scrollToLastMessage();
  });
}
/**
 * A send failure as a chat line. A server message in text is shown as before; a 422's validation
 * list is not text (it was handed to i18n.t() and broke the bubble) and is read only to say what
 * went wrong.
 */
private sendErrorMessage(error: any): string {
  const detail = error?.error?.detail ?? error?.error?.message;
  if (typeof detail === 'string' && detail.trim()) { return this.i18n.t(detail); }
  if (Array.isArray(detail)) {
    const tooLong = detail.find((d: any) => d?.type === 'string_too_long');
    if (tooLong) {
      return this.i18n.t('Your message is too long. Please keep it to %{max} characters or fewer.',
        { max: tooLong?.ctx?.max_length ?? MAX_MESSAGE_CHARS });
    }
  }
  return this.i18n.t('Something went wrong. Please try again.');
}

/**
 * The textareas stop at maxMessageChars themselves (native maxlength cuts a paste at the cursor
 * too); these say why, for a few seconds, whenever typing or a paste would have gone past it.
 */
onMessageBeforeInput(event: Event): void {
  const input = event as InputEvent;
  if (input.inputType !== 'insertText' && input.inputType !== 'insertCompositionText') { return; }
  this.noticeIfOverLimit(input.target as HTMLTextAreaElement, input.data || '');
}

onMessagePaste(event: Event): void {
  const paste = event as ClipboardEvent;
  this.noticeIfOverLimit(paste.target as HTMLTextAreaElement, paste.clipboardData?.getData('text/plain') || '');
}

get messageLimitNotice(): string {
  return this.i18n.t("You've reached the %{max}-character limit for a message.", { max: this.maxMessageChars });
}

private noticeIfOverLimit(field: HTMLTextAreaElement, incoming: string): void {
  if (!field || typeof field.value !== 'string' || !incoming) { return; }
  // Selected text is replaced by what comes in, so it does not count towards the total.
  const selected = Math.max((field.selectionEnd ?? 0) - (field.selectionStart ?? 0), 0);
  if (field.value.length - selected + incoming.length <= this.maxMessageChars) { return; }
  this.flashMessageLimitNotice();
}

/**
 * Mobile: the field has no native maxlength, because once a native limit is reached a phone keyboard
 * (composing words, predictive text) does not reliably report the keystrokes it blocks, so the notice
 * showed only the first time. Instead, an input that takes the message past the limit is cut back
 * here, and each such attempt shows the notice again.
 *
 * Only what was just inserted is cut, never the draft around it: the change is found by comparing
 * the new value with the last one within the limit (their common start and end are the draft that
 * stayed), and only as much of the inserted text is kept as still fits. Inserting into the middle of
 * a full draft therefore leaves the draft exactly as it was, end included.
 */
capMessageLength(event: any): void {
  const value: string = event?.detail?.value ?? event?.target?.value ?? '';
  if (typeof value !== 'string' || value.length <= this.maxMessageChars) { return; }
  const before = this.lastWithinLimit;
  let start = 0;
  while (start < before.length && start < value.length && before[start] === value[start]) { start++; }
  // Not between the two halves of an emoji (a surrogate pair): two emoji can share their first half.
  if (isLowSurrogate(value, start) && isHighSurrogate(value, start - 1)) { start--; }
  let end = 0;
  while (end < before.length - start && end < value.length - start
    && before[before.length - 1 - end] === value[value.length - 1 - end]) { end++; }
  if (end > 0 && isLowSurrogate(value, value.length - end) && isHighSurrogate(value, value.length - end - 1)) { end--; }
  const inserted = value.slice(start, value.length - end);
  const room = Math.max(this.maxMessageChars - start - end, 0);
  const kept = wholeCharactersWithin(inserted, room);
  const capped = value.slice(0, start) + kept + value.slice(value.length - end);
  const caret = start + kept.length;
  this.postMsg = capped;
  if (event?.target) {
    event.target.value = capped;
    // The caret goes where the kept insertion ends, as if only that had been typed.
    event.target.getInputElement?.()?.then?.((field: HTMLTextAreaElement) => field?.setSelectionRange?.(caret, caret));
  }
  this.flashMessageLimitNotice();
}

/** Shows the limit notice for a few seconds; a repeat restarts the count. */
private flashMessageLimitNotice(): void {
  this.showMessageLimitNotice = true;
  if (this.messageLimitNoticeTimer !== null) { clearTimeout(this.messageLimitNoticeTimer); }
  this.messageLimitNoticeTimer = setTimeout(() => {
    this.showMessageLimitNotice = false;
    this.messageLimitNoticeTimer = null;
  }, MESSAGE_LIMIT_NOTICE_MS);
}

typeWriterEffect(fullText: string, chatIndex: number, speed: number = 5, id) {
  if (this.activeTypingInterval !== null) {
    clearInterval(this.activeTypingInterval);
  }
  let currentIndex = 0;
  this.activeTypingInterval = setInterval(() => {
    if (currentIndex < fullText.length) {
      this.chat[chatIndex].message += fullText[currentIndex];
      currentIndex++;
      const el = this.scrollBottom?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
      //this.scrollToLastMessage();
    } else {
      clearInterval(this.activeTypingInterval!);
      this.activeTypingInterval = null;
      setTimeout(() => { this.addClickEventsToPosts(); }, 0);
      if (id && id.length > 0) {
        // Through the same gate as a restored transcript. Read straight from appState this matched
        // a fresh answer's citations against whatever bag the last cycle left behind — the same
        // fault that lost them on a restore, one screen along.
        this.withCycleLibrary(() => this.fetchCasualDocuments(id));
      }
      this.chat[chatIndex].noop = false;
      this.showAnimation = false;
      // Scroll after Angular has rendered the action bar and doc section.
      // setTimeout fires in the next macrotask, after zone.js CD has updated the DOM,
      // so scrollHeight correctly includes the newly visible action bar and docs.
      setTimeout(() => {
        const el = this.scrollBottom?.nativeElement;
        if (el) el.scrollTop = el.scrollHeight;
      }, 0);
    }
  }, speed);
}

  //  startTypingSequence() {
  //   this.generateTimings();

  //   this.messages.forEach((message, index) => {
  //     setTimeout(() => {
  //       this.typeMessage(message);
  //     }, this.timings[index]);
  //   });
  // }

  // private generateTimings(): void {
  //   this.timings = [];
  //   let currentTime = 0;

  //   const specificTimings = [2000, 6000, 4000, 2000];

  //   this.messages.forEach((msg, index) => {
  //       if (index < specificTimings.length) {
  //           this.timings.push(currentTime);
  //           currentTime += specificTimings[index] + this.messagePause; // Add pause after each message
  //       }
  //   });
  // }

  typeMessage(message: string) {
    const el = document.getElementById('typing-text');
    const cursor = document.getElementById('cursor');
    if (!el || !cursor) return;

    el.textContent = '';
     cursor.style.display = 'inline';
    let charIndex = 0;

    const interval = setInterval(() => {
      if (charIndex < message.length) {
        el.textContent += message.charAt(charIndex);
        charIndex++;
        this.scrollToLastMessage2();
      } else {
        clearInterval(interval);
        // this.moveToNextMessage();
      }
    }, this.typingDelay);
  }
// new code END
  /**
   * Closes the modal this panel is inside — that one, and not whichever happens to be on top.
   *
   * `ModalController.dismiss()` takes no target and dismisses the TOPMOST overlay, which is only
   * this panel while nothing has been presented since. The home sheet is an overlay too, and a
   * cycle switch rebuilds the whole main view: the sheet is presented again AFTER this panel, so it
   * becomes the topmost one while its own `z-index: 999` keeps it painted underneath. Closing the
   * panel then dismissed the sheet instead, and the learner was left on a home screen with no sheet
   * on it — the panel still open behind, needing a second tap.
   */
  closePage() {
    const own: any = this.el?.nativeElement?.closest?.('ion-modal');
    if (own?.dismiss) { own.dismiss(); return; }
    // Not inside a modal at all — the web layout mounts this panel beside the home screen.
    this.modalController.dismiss();
  }

  getFileIcon(type: string): string {
    switch (type) {
      case 'pdf': return 'assets/icon/pdf.svg';
      case 'video': return 'assets/icon/video.svg';
      case 'audio': return 'assets/icon/video.svg';
      default: return 'assets/icon/file.svg';
    }
  }
  async click() {
    this.appState.set("isHamburgerOpen", true);
    this.isModalOpen = this.appState.get("isHamburgerOpen");
    // console.log("called click event", this.isModalOpen);
  }
  closeHamModal() {
    this.appState.set("isHamburgerOpen", false);
    this.isModalOpen = this.appState.get("isHamburgerOpen");
    this.hamModal.closeHamModal();
  }
  async openFooziAiPopup() {
    const modal = await this.modalController.create({
      component: FooziAiLibraryPopupComponent,
      enterAnimation: this.animationService.slideInUpEnterAnimation,
      leaveAnimation: this.animationService.slideOutDownLeaveAnimation,
      cssClass: this.isMobile
        ? "m-foozi-Ai-library-popup"
        : "web-foozi-Ai-library-popup",
    });
    await modal.present();
    modal.onDidDismiss().then((res) => {
      if (res?.data?.isMsgSaved) {
        this.isMsgSaved = true;
      }
    });
  }
  // disableLike() {
  //   this.isDisableLike = true;
  //   this.chat.push({
  //     role: 'foozi-response',
  //     message: "<ion-text class=\"dislike-respond\"> I'm sorry you don't like that response. Please provide feedback about your experience so that my service can be improved. </ion-text>",
  //     class: 'foozi-responz-wrap'
  //   });
  //   setTimeout(() => {
  //     this.isDisableLike = false;
  //   }, 3000);
  //   console.log("isDisableLike", this.isDisableLike);
  // }
  like() {
    this.isLikeSelected = !this.isLikeSelected;
    // last two messages are the user input and the response from foozi
    let response = this.chat[this.chat.length - 1].message;
    let prompt = this.chat[this.chat.length - 2].message;
    this.fooziAiConciergeService.sendLike(response, prompt).subscribe((res) => {
      // console.log(res);
    });
    // console.log("like", this.isLikeSelected);
  }
  isDocumentAvailableInPath(docId) {
     let documents =this.appState.get("casualDocuments")
    let filteredDocuments = []
    if (docId?.length > 0) {
      filteredDocuments = documents?.filter(doc =>
        docId?.includes(doc?.id)
      );
    }
    else {
      filteredDocuments = documents?.filter(doc =>docId==doc?.id);
    }
    return filteredDocuments;
  }

  fetchCasualDocuments(docId) {
    //docId = docId?.length > 1 ? JSON.stringify(docId) : docId;
     let documents =this.appState.get("casualDocuments")
    let filteredDocuments = []
    filteredDocuments=documents?.filter(doc =>
                 docId?.includes(doc?.id)
    );
      filteredDocuments?.forEach((doc) => {
         if ((doc?.is_recommended && doc?.is_recommended === true) || (doc?.isRecommended && doc?.isRecommended ===true)) {
        const url = doc.link;
        let type;
        if (this.audioFormats.some(el => url.includes(el))) {
          type = 'audio'
        }
        else if (this.videoFormats.some(el => url.includes(el))) {
          type = 'video'
        }
        else {
          type = 'pdf'
        }
           const index = this.casulaDocumentsList?.findIndex(item => item.id === doc?.id) ;
          
               let casulaDocumentsList = this.casulaDocumentsList;
           if (index != -1) {
             casulaDocumentsList[index] = { ...doc, type: type, safeLink: doc?.editedDocumentUrl ? this.docLink(doc?.editedDocumentUrl) : this.docLink(url) };
           }
           else {
             casulaDocumentsList.push({ ...doc, type: type, safeLink: doc?.editedDocumentUrl ? this.docLink(doc?.editedDocumentUrl) : this.docLink(url) });
           }
              casulaDocumentsList = casulaDocumentsList.filter((item, index, self) =>
          index === self.findIndex(t => t.id === item.id)
             );
           this.casulaDocumentsList = [...casulaDocumentsList];
      }
      });
      // Update chat messages once after casulaDocumentsList is fully built.
      // Only set documents on messages that don't already have them — prevents
      // triggering SafeResourceUrl changes on old messages (causes iOS scroll jump).
      this.chat.forEach((msg: any, index: number) => {
        if (msg.role === 'foozi-response' && msg.sources?.length > 0 && !msg.documents) {
          const sourceIds = (msg.sources as any[]).map(s => +(s?.tool_id ?? s?.doc_id)).filter(Boolean);
          const matchedDocs = this.casulaDocumentsList.filter((doc: any) => sourceIds.includes(doc.id));
          this.chat[index] = { ...msg, documents: matchedDocs };
        }
      });
      this.loaded = true;
  }

  openFullScreenCasualDocuments(docId) {
    if (this.isDocumentAvailableInPath(docId)?.[0]?.id == docId || this.isDocumentAvailableInPath(docId)?.length ==docId?.length) {
       docId = docId?.length > 1 ? JSON.stringify(docId) : docId;
      this.fooziAiConciergeService.fetchDocumentDetails(docId).subscribe(async (res) => {
        if (res) {
          res['documents']?.forEach((doc) => {
            if ((doc?.is_recommended && doc?.is_recommended === true) ||(doc?.isRecommended && doc?.isRecommended ===true)) {
              const url = doc.link;
              let type;
              if (this.audioFormats.some(el => url.includes(el))) {
                type = 'audio'
              }
              else if (this.videoFormats.some(el => url.includes(el))) {
                type = 'video'
              }
              else {
                type = 'pdf'
              }
              // this.casulaDocumentsList= this.casulaDocumentsList?.filter(item => item.id !== doc.id)
              const index = this.casulaDocumentsList?.findIndex(item => item.id === doc?.id);
              let casulaDocumentsList = this.casulaDocumentsList;
           if (index != -1) {
             casulaDocumentsList[index] = { ...doc, type: type, safeLink: doc?.editedDocumentUrl ? this.docLink(doc?.editedDocumentUrl) : this.docLink(url) };
           }
           else {
             casulaDocumentsList.push({ ...doc, type: type, safeLink: doc?.editedDocumentUrl ? this.docLink(doc?.editedDocumentUrl) : this.docLink(url) });
           }
              casulaDocumentsList = casulaDocumentsList.filter((item, index, self) =>
          index === self.findIndex(t => t.id === item.id)
             );
           this.casulaDocumentsList = [...casulaDocumentsList];
              this.chat = this.chat.map(msg => {
                if (msg.role === 'foozi-response' && msg.sources?.length > 0) {
                  const sourceIds = (msg.sources as any[]).map((s: any) => +(s?.tool_id ?? s?.doc_id)).filter(Boolean);
                  const matchedDocs = this.casulaDocumentsList.filter((doc: any) => sourceIds.includes(doc.id));
                  return { ...msg, documents: matchedDocs };
                }
                return msg;
              });
              if (this.scrollBottom?.nativeElement) {
                this.scrollBottom.nativeElement.scrollTop = this.scrollBottom?.nativeElement?.scrollHeight;
              }
            }
            setTimeout(() => {
                this.scrollToLastMessage(); 
            },800)
          })
           this.loaded = true;
        }
      },
        (error) => {
          this.loaded = true;
        }
      )
    }
    else {
      this.loaded = true;
    }
  }
  openDocument(postDoc) {
    // console.log('post :', post);
    let journalPathName = this.path.name
    let posts = this.appState.get("casualDocuments")
    let doc =this.appState.get('singleDocument');
    let post;
    posts?.forEach((doc)=>{
      if(doc.id ==postDoc?.id){
        post =doc;
      }
    })
    // The row carries the document it was built from, and that is the same record. `casualDocuments`
    // is shared state anything may replace — app.component refetches it on every navigation — so an
    // attachment on screen can outlive its entry there, and looking it up was the only way this had
    // of knowing what to open: `post` stayed undefined and the next line read `.screenId` off it.
    post = post || postDoc;
    // Nothing openable. Better to do nothing than to throw on the way to a full-screen viewer.
    if (!post?.link) { return; }
    this.appState.set('singleDocument', post)
    this.appState.set('journalPost', undefined)
    if ((post as any)?.is_recommended === true || (post as any)?.isRecommended === true) {
      this.appState.set('isCasualDocument', true)
    }
    this.appState.set('journalPathName', journalPathName)
    let stepNumber = this.path?.findChapterStepByScreenContentId?.(this.appState.get('DailyStepsData'), post.screenId,true)?.stepPosition;
    this.appState.set('journalStepNumber', stepNumber)
    const url = post.link;
    this.appState.set('isDocumentOpenedFromFoozi', false);
    if (this.audioFormats.some(el => url.includes(el))) {
      // console.log('Audio File', this.appState.get('isMobile'));
      if (this.appState.get('isMobile')) {
        this.homeUiService.openNextComponent(FullScreenAudioComponent, { data: post })
      } else {
        this.homeUiService.openNextComponent(FullScreenAudioComponent, { data: post }, 'desktop-library-custom')
      }
    } else if (this.videoFormats.some(el => url.includes(el))) {
      // console.log('Video File', this.appState.get('isMobile'));
      if (this.appState.get('isMobile')) {
        this.homeUiService.openNextComponent(FullScreenVideoComponent, { data: post })
      } else {
        this.homeUiService.openNextComponent(FullScreenVideoComponent, { data: post }, 'desktop-library-custom')
      }
  
    } else {
      // console.log('Document', this.appState.get('isMobile'));
      if (this.appState.get('isMobile')) {
        this.homeUiService.openNextComponent(FullScreenDocumentComponent)
      } else {
        this.homeUiService.openNextComponent(FullScreenDocumentComponent, '', 'desktop-library-custom')
      }
  
    }
  }
    
  //Open Full Screen Routine
  openFullScreenRoutine(postId: number) {
    this.fooziAiConciergeService.getPostID(postId).subscribe(async (res) => {
      this.fooziAiConciergeService.getPostReplies(res["data"]["root_id"], res["data"]["mode"], res["data"]["path_id"],postId).subscribe(async (res2) => {
        let singleTask = res2['data'];
        let mode = singleTask["type"];
        if (this.isMobile) {
         if (mode == "Routine") {
           this.openRoutineFeed(singleTask,postId);
         } else if (mode == "Question") {
           this.openHabitQuestionFeed(singleTask, mode?.toLowerCase(),postId);
         } else if (mode == "Habit") {
           this.openHabitQuestionFeed(singleTask, mode?.toLowerCase(), postId);
         }
        }  
        else {
           const modal = await this.modalController.create({
             component: MobileChatBoardComponent,
             cssClass: "desktop-custom-modal",
             componentProps: { data: { isFocus: false, post: singleTask, replyId: postId } },
             enterAnimation: this.animationService.slideInLeftEnterAnimation,
             leaveAnimation: this.animationService.SlideOutRightLeaveAnimation,
           });
           this.homeUiService.setModalStack(modal);
           await modal.present();
           modal.onDidDismiss().then(() => {
             this.isModalOpen = false;
           });
        }
       
      });
    });
  }
  async openHabitQuestionFeed(singleTask, mode, replyId) {
    let selectedOption = this.appState.updateAcrossSteps(this.appState
      .get("DailyStepsData")
      ,mode + "s", singleTask.id, {
        votesCounts: singleTask.replyVotesCount,
      });

    if (selectedOption) {
      const data = {
        question: singleTask,
        modes: mode,
      };
      this.appState.set("singleFeed", data);
      // console.log("singleFeed :", data);
      this.appState.set("singleHabit", singleTask);
    }

    const modal = await this.modalController.create({
      component: FullScreenFeedComponent,
      // animated: false,
      componentProps: { data: singleTask, focuseTextArea: false, replyId: replyId },
      cssClass: 'custom-modal',
      enterAnimation: this.animationService.slideInLeftEnterAnimation,
      leaveAnimation: this.animationService.SlideOutRightLeaveAnimation,

    })
    await modal.present()
  }
  async openRoutineFeed(singleTask, replyId) {
    let selectedOption = this.appState.updateAcrossSteps(this.appState
      .get("DailyStepsData")
      ,"routineInfo", singleTask['routine_info_id'], {
        votesCounts: singleTask['votescount'],
      });

    if (selectedOption) {
      const dataFeed = {
        routine: singleTask,
        mode: "routineInfo",
      };

      this.appState.set("singleFeed", dataFeed);
    }

    let data = {
      mode: 'routineInfo',
      singleRoutine: singleTask
    }
    this.appState.set("singleRoutineFeed", data);

    const modal = await this.modalController.create({
      component: FullScreenRoutineComponent,
      animated: false,
      componentProps: { data: singleTask, focuseTextArea: true, replyId: replyId },
      cssClass: 'custom-modal',
      enterAnimation: this.animationService.slideInLeftEnterAnimation,
      leaveAnimation: this.animationService.SlideOutRightLeaveAnimation,
    })
    await modal.present();
  }
  dislike() {
    this.dislikeFeedback = true;
    this.isLikeSelected = false;
     if (this.chat.length >= 1) {
       this.chat[this.chat.length - 1].noop = true;
     }  
     const msg = this.i18n.t('I’m sorry you don’t like that response. Please provide feedback about your experience so that my service can be improved.')  
    this.chat.push({
      role: 'foozi-response',
      message: `<ion-text class=\"dislike-respond\"> ${msg} </ion-text>`,
      class: 'foozi-responz-wrap',
      noop: true
    });
     setTimeout(() => {
       this.scrollToLastMessage();
     }, 200);
    // console.log("dislike", this.dislikeFeedback);
  }
   extractPlainTextFromHtml(html: string): string {
    const tempDiv = document.createElement('div');
    // Block boundaries carry meaning in copied text — textContent on its own would run
    // paragraphs, headings and list items together on a single line.
    tempDiv.innerHTML = (html || '')
      .replace(/<\/(p|div|li|h[1-6]|blockquote|tr)>/gi, '$&\n')
      .replace(/<br\s*\/?>/gi, '\n');
    return (tempDiv.textContent || tempDiv.innerText || '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }
  async copyContent(text) {
    // The bubble stores raw markdown — render it first so the clipboard gets formatted
    // text instead of the ** and * markers.
    const plainText = this.extractPlainTextFromHtml(markdownToHtml(text));
   if (this.platform.is('cordova')) {
      try {
        await this.clipboard.copy(plainText).then(() => {
          this.isCopyContent = true;
        })
      } catch (error) {
        this.toaster.error(this.i18n.t('Error copying to clipboard with Cordova'));
      }
    } else {
      try {
        await navigator.clipboard.writeText(plainText).then(() => {
          this.isCopyContent = true;
        })
      } catch (error) {
         this.toaster.error(this.i18n.t('Error copying to clipboard with web API'));
      }
   }
     setTimeout(() => {
       this.isCopyContent = false;
     }, 700);
  }
   @HostListener('touchstart', ['$event'])
   onTouchStart(event: TouchEvent) {
    if (this.platform.is('ios')) {
      this.touchStartY = event.touches[0].clientY;
    }
  }

  @HostListener('touchend', ['$event'])
  onTouchEnd(event: TouchEvent) {
    let ele = document.getElementsByClassName("foozi-dynamic-textarea");
    if (this.rowCount > 1 && ele) {
      // ele[0].setAttribute("style", "margin-bottom:5px !important;");
    } else {
      // ele[0].setAttribute("style", "margin-bottom:-20px !important;");
    }
    if (this.platform.is('mobileweb')) {
      this.addInputEventListeners();    
    } 
    if (this.platform.is("ios") || this.platform.is("android")) {
      const touchEndY = event.changedTouches[0].clientY;
      // console.log("this.touchStartY, touchEndY", this.touchStartY, touchEndY);
      // Check if the touch started and ended at the top of the screen
      if (this.touchStartY < 65 && touchEndY < 65) {
        this.scrollToTop();
      }
    }
  }
  @HostListener("click", ["$event"])
  onLinkClick(e) {
   if (this.platform.is("mobileweb")) {
      this.addInputEventListeners();
    }
  }

  scrollToTop() {
    if (this.foozicardContainer) {
      const container = this.foozicardContainer.nativeElement;
      if (container) {
        container.scrollTo({ top: 0, behavior: 'smooth', duration:200 });
      }
    }
  }
  sanitizeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html || '');
  }

  // ─── #2 — In-step action card handlers ──────────────────────────────────────

  /** See markCompletedAction for what `echoToChat` decides. */
  async finalizeInStepAction(echoToChat = true): Promise<void> {
    const state = this.microActionState;
    if (!state) return;
    if (!state.screenContentId) {
      this.toaster.error(this.i18n.t('Please open the micro-action to commit this action'));
      return;
    }
    await this.openAndScheduleCalendar(state);
    this.saveCommitAction(state, echoToChat);
  }

  // Handles "I completed this" — calls the same API as habitConfirm() in the
  // parent daily-steps component (schedule_commit_action_kyper with completion=true).
  /**
   * `echoToChat` is the difference between the panel's own chips and the micro-action screen opened
   * from History: both run this flow, but only the first should leave a system notice in the
   * conversation on screen. The second would put it in a conversation nobody was looking at.
   */
  markCompletedAction(onSuccess?: () => void, echoToChat = true) {
    const state = this.microActionState;
    if (!state) return;
    // Completion requires valid server IDs. If state is from the minimal last-resort fallback
    // (fresh login, server didn't propagate scid), direct the user to open the chatbot first.
    if (!state.screenContentId) {
      this.toaster.error(this.i18n.t('Please open the micro-action to complete this action'));
      return;
    }
    const todoMatch = this.findMatchingTodoAction();
    const wasAlreadyCompleted = this.isActionCompleted && (!todoMatch || todoMatch.userActionCompletion);
    if (wasAlreadyCompleted) {
      this.toaster.error(this.i18n.t('Micro-action is already completed'));
      return;
    }
    const input = {
      step_id: state.stepId,
      screen_id: state.screenId,
      screen_content_id: state.screenContentId,
      user_action_completion: true,
      // customization_id: matchedAction?.customizationId,
      action_date_time: this.kyperApiService.formatDate(),
    };
    const completeTitle = state.currentAction?.title || '';
    const completeTitleHtml = completeTitle ? `<span class="system-action-highlight">${completeTitle}</span>` : '';
    const completeMsg = completeTitle
      ? `Action: ${completeTitleHtml} marked as complete`
      : 'Action marked as complete';
    this.kyperApiService.schedule_commit_action_kyper(input)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res: any) => {
          if (echoToChat) {
            this.chat.push({
              role: 'system',
              message: completeMsg,
              class: 'info-event-wrap',
            });
            setTimeout(() => this.scrollToLastMessage1(), 100);
          }
          this.toaster.success(this.i18n.t('Micro-action completed'));
          // Lets a screen hosting this action (the History modal) record its own completion event,
          // and only once the write actually succeeded.
          onSuccess?.();
          const pathId = this.path?.id;
          const finalizeComplete = () => {
            // The hosting screen records its own event through onSuccess above, so ours would be the
            // same system turn on the same thread a second time. The state below still has to be
            // updated either way — it is what the chips read.
            if (echoToChat) {
              // Action session, not the chat session — no cursor bump. See the commit path.
              this.appendActionEvent(state, {
                role: 'system',
                type: 'action_completed',
                content: completeMsg,
                screen_content_id: state.scid,
              }).pipe(takeUntil(this.destroyed$)).subscribe();
            }
            const updatedState = { ...state, isActionCompleted: res?.data?.userActionCompletion ?? true };
            this.microActionStateService.update(updatedState);
            sessionStorage.setItem('ma_state', JSON.stringify(updatedState));
          };
          const afterRefresh = () => {
            if (this.path?.enabledAttributes?.feedback && !wasAlreadyCompleted) {
              // APP-3074 — the completion response is the only place that knows WHICH
              // kyper_user_actions row was just written, and which customized version went onto it.
              // That identity was being dropped here, leaving the feedback modal with step/screen
              // identity alone — which matches every completed row of this Action, not this one.
              this.openCompletedFeedbackModal(
                {
                  ...state,
                  actionId: res?.data?.actionId,
                  customizationId: res?.data?.committedCustomizationId,
                },
                finalizeComplete
              );
            } else {
              finalizeComplete();
            }
          };
          if (pathId) {
            this.kyperApiService.getActionsList({ path_id: pathId })
              .pipe(takeUntil(this.destroyed$))
              .subscribe({
                next: (data) => { this.appState.set('todoActions', data?.actions); afterRefresh(); },
                error: () => afterRefresh(),
              });
          } else {
            afterRefresh();
          }
        },
        error: () => {
          this.toaster.error(this.i18n.t('Failed to mark action as complete. Please try again.'));
        },
      });
  }

  /**
   * The header's "new chat" icon — and the same path used when the app is opened fresh.
   *
   * Clears the panel to an empty conversation with no suggested questions, then mints a session on
   * the server so the first message is stored against it. The suggestions are deliberately dropped:
   * they are the greeting for a brand-new session, and the learner asked for a blank start.
   */
  async startFreshSession(): Promise<void> {
    // The header's new-chat button: a clean screen, with no greeting and no chips. The learner
    // asked for nothing there, not a greeting with its questions removed.
    this.resetPanel({ greeting: false });
  }

  /**
   * Empties the panel back to a conversation that has not started.
   *
   * Nothing is created here. Opening a session for a panel the learner may never type into leaves
   * an empty conversation in their history — the API's own integrator note warns against exactly
   * that — so the first message opens one instead (see ensureSession).
   *
   * `greeting` is the difference between the two ways of arriving at a blank panel: a fresh app
   * open shows the greeting and its suggested questions, the header's new-chat button shows neither.
   */
  private resetPanel({ greeting }: { greeting: boolean }): void {
    this.chat = [];
    this.postMsg = '';
    this.serverMessageCount = 0;
    this.serverMessageSession = '';
    this.isLoader = false;
    this.showWelcome = greeting;
    this.openedFromHistory = false;
    // Suggested questions on every empty panel — a fresh start and the header's new chat alike.
    this.suggestionsAllowed = true;
    // Anything still in flight was asked for the panel being cleared.
    this.suggestionsRequestId++;
    this.suggestions = [...DEFAULT_SUGGESTIONS];
    // Rendered first so the panel is never chipless, then replaced by the learner's own step,
    // phase and cycle as soon as the server answers.
    this.loadSuggestedQuestions();
    // Everything below belongs to the conversation being left behind. Missed, these carry into the
    // new one: an action's chips stay anchored, a recommit prompt stays answered, cited documents
    // stay listed under a transcript that no longer exists.
    this.casulaDocumentsList = [];
    // A library still on its way for the conversation being left behind must not fill this panel.
    this.documentsRequestId++;
    this.fooziRecommitAccepted = false;
    this.fooziRecommitDeclined = false;
    this.microActionState = null;
    this.microActionDescHtml = null;
    sessionStorage.removeItem('ma_state');
    sessionStorage.removeItem('foozi_session');
    sessionStorage.removeItem('foozi_session_created');
    this.microActionStateService.clear();
    // Anything watching the session's validity is watching the old one — the banner it drives has
    // nothing to say about a conversation that has not started.
    this.fooziAiConciergeService.setsessionValidityStatus(false);
    // Tells anything holding the old conversation to let go of it now, rather than on its next open.
    this.fooziAiConciergeService.setActiveSession(null);
    // Until the first message lands, ignore whatever is in sessionStorage: another screen opened
    // from here (the micro-action chatbot) resolves a session for its own bookkeeping and writes
    // that key, and the learner's next message must not join the conversation they just left.
    this.wantsNewConversation = true;
    this.rememberMode(greeting ? 'fresh' : 'blank');
    // Outlives the mode: this conversation keeps the screen it was started on, message or no message.
    this.rememberGreeting(greeting);
    // No conversation is on screen any more, so there is nothing for the per-conversation record to
    // describe. Left behind, it would decide the screen for whichever conversation this panel
    // happens to adopt next.
    try { sessionStorage.removeItem(PANEL_GREETING_SESSION_KEY); } catch { /* nothing stored */ }
    this.cdRef.detectChanges();
  }

  /**
   * The session to send a message against, opened on demand.
   *
   * A blank panel has no session behind it until there is something to store in one, so the first
   * message settles it — in this order:
   *
   *  1. the one this run is already using;
   *  2. the active session on the server IF nothing was ever said in it — a session opened and never
   *     used is the same thing this message needs, so it is taken over rather than left behind as an
   *     empty row while a second one is created beside it;
   *  3. otherwise a new one, because an active session that DOES have messages is last night's
   *     conversation and the learner asked to start clean.
   */
  private ensureSession(): Observable<string> {
    const existing = this.serverMessageSession
      || (this.wantsNewConversation ? '' : sessionStorage.getItem('foozi_session'));
    if (existing) { return of(existing); }

    const wanted = this.greetingWanted();
    return this.fooziAiConciergeService.checkResumeSessionValidity().pipe(
      // 404 "no active session" is the expected answer on first use, not an error.
      catchError(() => of(null)),
      switchMap((res: any) => {
        const activeId = res?.session_id;
        const hasMessages = (res?.messages || []).length > 0;
        if (activeId && !hasMessages && this.sessionSuitsScreen(res, wanted)) {
          return of(this.adoptSession(activeId, 'reused empty session'));
        }
        return this.fooziAiConciergeService.createChatSession(undefined, wanted)
          .pipe(map((created: any) => this.adoptSession(created?.session_id, 'first message')));
      }),
    );
  }

  /**
   * Whether an untouched session already on the server can hold what the learner is about to say.
   *
   * `show_default_questions` is fixed when the session is opened and nothing changes it afterwards,
   * so taking over one opened on a greeted screen would put the intro and its questions back above a
   * conversation the learner deliberately started blank — on every reopen, on every device. Where
   * the API sends no flag there is nothing to disagree with and the reuse stands, which is what
   * keeps an unused session from being left behind as an empty row in their history.
   */
  private sessionSuitsScreen(response: any, wanted: boolean): boolean {
    const stored = this.showDefaultQuestionsOf(response);
    return stored === null || stored === wanted;
  }

  /** Records the session this run will store its conversation in. */
  private adoptSession(sessionId: string, reason: string): string {
    const id = sessionId || '';
    // While greetingWanted() still answers for the screen this conversation was started on: the
    // panel is about to become an ordinary 'session', and this is the only moment the blank start
    // is known by both its id and its origin. Belt and braces behind the session's own
    // `show_default_questions` — it answers for conversations opened against an API without it.
    if (!this.greetingWanted()) { this.rememberBlankStart(id); }
    // The panel already has its screen; this is the conversation it now belongs to, so a cycle
    // switch a moment later restores the two together.
    this.rememberGreetingForSession(id, this.showWelcome);
    this.serverMessageSession = id;
    this.wantsNewConversation = false;
    sessionStorage.setItem('foozi_session', id);
    sessionStorage.setItem('foozi_session_created', Date.now().toString());
    this.fooziAiConciergeService.setActiveSession(id);
    this.rememberMode('session');
    this.setupPageLeaveSync();
    mixpanel.track('Foozi AI: New session created', { session_id: id, reason });
    return id;
  }

  /** Records whether this conversation carries the greeting, for the refresh to put back. */
  private rememberGreeting(on: boolean): void {
    try {
      sessionStorage.setItem(PANEL_GREETING_KEY, on ? 'on' : 'off');
    } catch {
      // Private-mode storage failures cost the learner their place on a refresh, nothing more.
    }
  }

  /** Whether the conversation being put back should carry the greeting. Nothing stored: it does. */
  private greetingWanted(): boolean {
    return sessionStorage.getItem(PANEL_GREETING_KEY) !== 'off';
  }

  /**
   * The screen this run is on, or null when this run has not been on one yet.
   *
   * The difference matters: a panel that has been used this run — a cycle switch, a refresh, a
   * blank start — knows first-hand what the learner is looking at, and nothing the server says
   * about a session should move it out from under them. A new tab or a fresh launch knows nothing,
   * and the server's flag is then the best answer there is.
   */
  private greetingRemembered(): boolean | null {
    const stored = sessionStorage.getItem(PANEL_GREETING_KEY);
    return stored === null ? null : stored !== 'off';
  }

  /** What this run last showed above THIS conversation, or null if it has not shown it. */
  private greetingRememberedFor(sessionId: string): boolean | null {
    if (!sessionId) { return null; }
    try {
      const stored = sessionStorage.getItem(PANEL_GREETING_SESSION_KEY) || '';
      const at = stored.lastIndexOf(':');
      if (at < 1) { return null; }
      return stored.slice(0, at) === sessionId ? stored.slice(at + 1) !== 'off' : null;
    } catch {
      return null;
    }
  }

  /** Records it, so a refresh or a cycle switch puts the same thing back. */
  private rememberGreetingForSession(sessionId: string, on: boolean): void {
    if (!sessionId) { return; }
    try {
      sessionStorage.setItem(PANEL_GREETING_SESSION_KEY, `${sessionId}:${on ? 'on' : 'off'}`);
    } catch {
      // Private mode, or storage full: the panel still renders, it just cannot promise to come
      // back the same way.
    }
  }

  /**
   * Whether the greeting block belongs above this conversation.
   *
   * One answer, everywhere: `show_default_questions`, which this app set when it opened the session
   * and the server has handed back unchanged ever since. It records the screen the conversation was
   * started on — the blank one or a greeted one — and it says the same thing on a refresh, after a
   * cycle switch, when History reopens it, and on the learner's other device. Nothing local is
   * consulted while it is there, and it never moves: a conversation that was greeted still reads
   * true after fifty messages.
   *
   * Everything under it is for conversations that predate the flag — the ones already open when this
   * shipped, and all of them until the API carrying it is deployed. Those fall back to what this
   * browser recorded at the time, and the block can be deleted once no such conversation is in reach.
   */
  private greetingFor(
    sessionId: string,
    response: any,
    messageCount: number,
    { runScreen = null, fallback = true }:
      { runScreen?: boolean | null; fallback?: boolean } = {},
  ): boolean {
    const stored = this.showDefaultQuestionsOf(response);
    if (stored !== null) { return stored; }

    // ── No flag on this session. Everything below is the pre-flag fallback. ──────────────────────

    // 1 · What this run already showed above THIS conversation. A cycle switch or a refresh is
    //     putting the panel back, not deciding it again.
    const remembered = this.greetingRememberedFor(sessionId);
    if (remembered !== null) { return remembered; }

    // 2 · A conversation the learner themselves started on the clean screen stays clean. It is a
    //     fact about the conversation, not about how it was opened, so it holds for a cycle switch
    //     putting it back as much as for History reopening it.
    if (this.wasStartedBlank(sessionId)) { return false; }

    // 3 · The screen this run is on, where the caller is PUTTING BACK a conversation rather than
    //     opening one. History passes nothing here — reopening a finished conversation is not
    //     putting a screen back.
    if (runScreen !== null && runScreen !== undefined) { return runScreen; }

    // 4 · The transcript. A conversation that has been spoken in is past its greeting; an untouched
    //     one falls back to the screen this run is on.
    return messageCount === 0 ? fallback : false;
  }

  /**
   * `show_default_questions` off a session response, or null where the API has not sent one.
   *
   * Only this field. The `foozi_panel_greeting` it replaces is deliberately NOT read as a stand-in:
   * that one was server-derived and turned itself false on a session's first message, so honouring
   * it would strip the greeting from every conversation the learner has spoken in — the opposite of
   * what the flag now means.
   */
  private showDefaultQuestionsOf(response: any): boolean | null {
    const flag = response?.show_default_questions;
    return typeof flag === 'boolean' ? flag : null;
  }

  private blankStartedIds(): string[] {
    try {
      const stored = JSON.parse(localStorage.getItem(BLANK_STARTED_KEY) || '[]');
      return Array.isArray(stored) ? stored.filter((id: any) => typeof id === 'string') : [];
    } catch {
      return [];   // truncated or hand-edited: no record, which is the default anyway
    }
  }

  /** Notes that this conversation began on the clean screen, so History can put it back that way. */
  private rememberBlankStart(sessionId: string): void {
    if (!sessionId) { return; }
    const ids = this.blankStartedIds().filter((id) => id !== sessionId);
    ids.push(sessionId);
    try {
      localStorage.setItem(BLANK_STARTED_KEY, JSON.stringify(ids.slice(-BLANK_STARTED_MAX)));
    } catch {
      // Storage blocked or full. The conversation reads as an ordinary one from History.
    }
  }

  private wasStartedBlank(sessionId: string): boolean {
    return !!sessionId && this.blankStartedIds().includes(sessionId);
  }

  /**
   * Reopens the micro-action the learner asked for before switching cycles.
   *
   * The switch routes and rebuilds the app, so nothing that could hold the request survives it; the
   * action's id is left in sessionStorage and collected here. History opens behind it, which is
   * where closing the action then returns them — the list they came from, not a home screen.
   */
  private openActionAfterCycleSwitch(): void {
    let requested: string | null = null;
    try {
      requested = sessionStorage.getItem(OPEN_ACTION_AFTER_SWITCH_KEY);
      // One shot. Left behind it would reopen on every panel open from here on.
      sessionStorage.removeItem(OPEN_ACTION_AFTER_SWITCH_KEY);
    } catch {
      // Storage unavailable: there was no request to honour anyway.
    }
    // A tick, so History is never created in the middle of this panel's own init — on mobile the
    // panel is itself a modal that is still presenting at this point.
    if (requested) { setTimeout(() => this.openHistory(requested)); }
  }

  /** Records how the panel was left, so a refresh or a path switch comes back to it. */
  private rememberMode(mode: PanelMode): void {
    try {
      sessionStorage.setItem(PANEL_MODE_KEY, mode);
    } catch {
      // Private-mode storage failures cost the learner their place on a refresh, nothing more.
    }
  }

  /**
   * Guards against a late reply landing on a panel that has moved on — a second open, a new chat,
   * or a cycle switched while the request was in flight.
   */
  
  /**
   * Starter chips for a panel that has no session yet.
   *
   * The server templates these from the learner's current step, phase and cycle, so they speak to
   * where the learner actually is; DEFAULT_SUGGESTIONS is the same three lines for everyone. The
   * call creates no session and writes nothing, so lazy session creation is preserved and it is
   * safe on every panel open.
   *
   * Whatever is already on screen stays if the request fails — the hardcoded set is the floor, never
   * an empty greeting. The endpoint itself answers with a generic set rather than erroring when it
   * cannot look the learner up, so only a transport failure reaches the error path.
   */
  private loadSuggestedQuestions(): void {
    if (!this.suggestionsAllowed) { return; }
    const requestId = ++this.suggestionsRequestId;
    const askedForPath = this.appState.get('currentPath')?.id ?? null;
    this.suggestionsPathId = askedForPath;
    this.fooziAiConciergeService.getSuggestedQuestions().subscribe(
      (res: any) => {
        // A newer request, a conversation still loading over this one, or a cycle switched under the
        // reply: the chips would belong to a screen the learner has left.
        if (requestId !== this.suggestionsRequestId) { return; }
        if (!this.suggestionsAllowed) { return; }
        if ((this.appState.get('currentPath')?.id ?? null) !== askedForPath) { return; }
        const chips = (res?.suggested_questions || []).filter(Boolean);
        if (chips.length) { this.suggestions = [...chips]; }
      },
      () => { /* keep the chips already on screen */ },
    );
  }

  /**
   * Fills the chip row under the opening line, once the conversation on screen has settled.
   *
   * Every conversation gets them — a fresh start, the header's new chat, one reopened from History —
   * whatever its `show_default_questions` says; that flag now decides only the opening line.
   */
  private offerSuggestions(fromServer?: any[]): void {
    this.suggestionsAllowed = true;
    const offered = (fromServer || []).filter(Boolean);
    // Shown while the request below is in flight, so the greeting is never chipless.
    this.suggestions = offered.length ? [...offered] : [...DEFAULT_SUGGESTIONS];
    // Always asked for: GET /suggested_questions reflects where the learner is now, while a session's
    // stored set is whatever was current when it was opened. Its answer replaces either set above.
    this.loadSuggestedQuestions();
  }

  // ── Conversation history ─────────────────────────────────────────────────────
  // Entry point is the header's clock icon. The modal itself only lists and reports what was
  // tapped; navigation lives here, because only this component knows how to load a session into the
  // panel behind it, and because two of the three tabs need the history modal to stay open.

  async openHistory(openActionId: number | string | null = null): Promise<void> {
    const modal = await this.modalController.create({
      component: FooziHistoryModalComponent,
      componentProps: {
        // Set only on the return leg of a cycle switch — see openActionAfterCycleSwitch.
        openActionId,
        // The micro-action chips need flows that live here, not in the modal: the panel already runs
        // all three for its own chips, against the action state the in-step screen publishes.
        // echoToChat false: the learner is acting in the micro-action screen, not in the
        // conversation behind it, so no system notice is written there.
        onCommit: (done?: () => void) => this.finalizeInStepAction(false).then(() => done?.()),
        onOpenTool: () => this.openFooziToolLink(),
        onMarkCompleted: (done?: () => void) => this.markCompletedAction(done, false),
      },
      cssClass: this.isMobile
        ? 'pratice-bot-custom-modal'
        : 'web-daily-steps-custom-modal web-pratice-bot-custom-modal',
    });
    await modal.present();

    // Only a chat comes back here — it replaces the conversation behind this modal, so the modal
    // closed itself. Micro-actions and practice open on top of it and never dismiss it, which is
    // what keeps the learner's place in the list while they look at one.
    const { data, role } = await modal.onDidDismiss();
    const selection: FooziHistorySelection | null = data ?? null;
    if (selection?.tab === 'chats') {
      this.loadSessionIntoPanel(selection.row?.session_id, { fromHistory: true });
      return;
    }

    // The learner tapped the conversation they were already in. Nothing behind the modal has moved,
    // so there is nothing to re-read — and re-reading it would treat what is on screen as a
    // conversation being opened from History, chips and greeting decided all over again.
    if (role === HISTORY_DISMISS_CURRENT) { return; }

    // Nothing else to do. Closing History cannot have changed the conversation behind it: every
    // write reachable from that modal goes somewhere else now. A micro-action's refine, commit and
    // completion events are stored on the action's own session (resolved by screen_content_id — see
    // actionSessionFor, and ensureSessionId in the in-step screen), practice writes practice
    // sessions, and renaming a chat only touches localStorage. The panel used to re-read its own
    // session here, from when those action events did land in the conversation; it now finds nothing
    // new every time. The one case it would still catch — the same conversation continued in
    // another browser tab — was never what it was for, and the in-app catch-up it was standing in
    // for is setupPageLeaveSync.
  }

  /**
   * The session a micro-action's turns belong to, resolved by the action rather than by whatever
   * conversation is on screen.
   *
   * Resolved once and kept on the shared state, so the chips, the refine box and the event writes
   * all address the same thread. An action with no id to resolve by — the last-resort state built
   * from a card whose screen_content_id the server did not propagate — yields '' and the caller
   * skips the write rather than sending it to the conversation.
   */
  private actionSessionFor(state: MicroActionSessionState): Observable<string> {
    if (state?.sessionId) { return of(state.sessionId); }
    const screenContentId = state?.screenContentId || state?.scid;
    if (!screenContentId) { return of(''); }

    return this.fooziAiConciergeService.resolveActionSession(screenContentId).pipe(
      map((sessionId: string) => {
        if (sessionId) {
          const updated = { ...state, sessionId };
          this.microActionStateService.update(updated);
          sessionStorage.setItem('ma_state', JSON.stringify(updated));
        }
        return sessionId;
      }),
      catchError(() => of('')),
    );
  }

  /**
   * Records an action event on that action's own thread, resolving it first if this run has not
   * needed it yet. Skipped rather than misfiled when there is no action to resolve by.
   */
  private appendActionEvent(state: MicroActionSessionState, event: any): Observable<any> {
    return this.actionSessionFor(state).pipe(
      switchMap((sessionId) => (sessionId
        ? this.fooziAiConciergeService.appendSessionEvent(sessionId, event)
        : of(null))),
    );
  }

  /**
   * Replace the panel's conversation with a past one.
   *
   * Shared by two callers that want different things above the transcript: a refresh or a path
   * switch is putting back the conversation the learner is in, while History is opening one they
   * have finished with — hence fromHistory.
   */
  private loadSessionIntoPanel(sessionId: string, { fromHistory = false } = {}): void {
    if (!sessionId) { return; }
    this.isLoader = true;
    // Not known until the session answers whether it shows questions — see suggestionsAllowed.
    const allowedBefore = this.suggestionsAllowed;
    this.suggestionsAllowed = false;
    this.suggestionsRequestId++;
    this.fooziAiConciergeService.checkSessionValidity(sessionId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
          (res: any) => {
            this.chat = this.mapSessionMessages(res?.messages || []);
            // The documents this transcript cites. Never done here at all, so a conversation with a
            // PDF in it came back without it — the rows carry `sources` (ids) and the template
            // renders an attachment from `documents`, which only this sets. Reopened from History
            // that meant no attachment and, under it, the "Loading preview…" skeleton for good.
            // Cleared first: the conversation being replaced took its own documents with it.
            this.casulaDocumentsList = [];
            this.attachCitedDocuments(res?.messages || []);
            // Reopened from History: the screen THAT conversation was started on — a blank start
            // comes back as its transcript alone, anything else with the intro and its questions
            // above it. Otherwise the screen this run started on, which a refresh has to put back.
            const messageCount = (res?.messages || []).length;
            // Two different questions, deliberately kept apart.
            //
            // `wanted` is the screen this LEARNER is on — what the next conversation this panel
            // opens will be created with, and the only thing remembered under PANEL_GREETING_KEY
            // below. Reading a bare chat out of History does not make them someone who wants bare
            // chats. `greeting` is what belongs above THIS conversation: its own
            // `show_default_questions`, or, for one opened before that flag existed, what this
            // browser recorded about it at the time.
            const wanted = fromHistory ? !this.wasStartedBlank(sessionId) : this.greetingWanted();
            const greeting = this.greetingFor(sessionId, res, messageCount, {
              // Only a restore is putting a screen back; History is opening a conversation.
              runScreen: fromHistory ? null : this.greetingRemembered(),
              fallback: wanted,
            });
            this.showWelcome = greeting;
            this.openedFromHistory = fromHistory;
            // Against the conversation, not the run: a cycle switch reopens this same one and has
            // to put back what was above it — including nothing, for one reopened from History.
            this.rememberGreetingForSession(sessionId, greeting);
            this.serverMessageSession = sessionId;
            this.serverMessageCount = (res?.messages || []).length;
            sessionStorage.setItem('foozi_session', sessionId);
            // Stamped with the rest, as every other adoption does — the resume path included, which
            // also stamps "now" for a conversation the server opened long before. Without it
            // checkIsSessionExpired reads an id whose age is unknowable, reports it due for
            // replacement, and watchSessionValidity quietly swaps the stored id for the server's
            // NEWEST conversation. Nothing on screen moves, so it goes unnoticed until the next
            // re-init — a refresh, or a cycle switch — comes back on a conversation the learner
            // never chose instead of the one they opened from History.
            sessionStorage.setItem('foozi_session_created', Date.now().toString());
            this.fooziAiConciergeService.setActiveSession(sessionId);
            this.rememberMode('session');
            this.wantsNewConversation = false;
            this.offerSuggestions(res?.suggested_questions);
            // The conversation on screen is now this one, so a refresh has to reach the same answer.
            if (fromHistory) { this.rememberGreeting(wanted); }
            this.isLoader = false;
            // The transcript may carry action cards — an older conversation holds its customisation
            // turns inline. Without this the cards render but their chips do not, which is what the
            // resume path has always done here.
            this.tryRestoreMicroActionState();
            this.cdRef.detectChanges();
            this.addClickEventsToPosts();
            this.scrollToBottomWhenSettled();
          },
          () => {
            this.isLoader = false;
            // Nothing was replaced, so what was on screen still decides.
            this.suggestionsAllowed = allowedBefore;
            this.toaster.error(this.i18n.t('We could not open that conversation.'));
            this.cdRef.detectChanges();
          },
      );
  }

  /**
   * Loads the documents a stored transcript cites and hangs them off the rows citing them.
   *
   * Every path that puts a conversation back on screen needs this. A rebuilt row carries `sources`
   * — the ids — and nothing more, while the attachment itself renders from `documents`, which only
   * fetchCasualDocuments sets. A transcript that does not pass through here shows no attachment and
   * leaves `loaded` false, so the skeleton standing in for it waits on a load never started.
   */
  private attachCitedDocuments(messages: any[]): void {
    // Bumped for every transcript put on screen, including one that cites nothing: that is exactly
    // the conversation a library still on its way for the PREVIOUS one would otherwise fill.
    const requestId = ++this.documentsRequestId;
    const ids = [...new Set<number>((messages || []).reduce(
      (acc: number[], message: any) => acc.concat(this.docIdsFrom(message)), [],
    ))];
    if (ids.length === 0) {
      // Nothing cited anywhere in it, so no load is coming: release the skeleton rather than
      // leaving it up for good.
      this.loaded = true;
      return;
    }
    this.withCycleLibrary(() => {
      if (requestId !== this.documentsRequestId) {
        // A different conversation is on screen now. Attaching here would hang this transcript's
        // documents off that one's rows and leave its library behind under them.
        return;
      }
      this.fetchCasualDocuments(ids);
    });
  }

  /**
   * Runs `use` against a document library belonging to the cycle the learner is in NOW.
   *
   * fetchCasualDocuments resolves ids against `casualDocuments` in appState, and that shared bag is
   * the whole difficulty. One switch path clears it and leaves app.component to fetch the new
   * cycle's on navigation, which this panel does not wait for — restored in that window a
   * conversation matched against nothing, came back with its attachment missing and never retried,
   * which is why the same conversation opened from History minutes later had it. Another switch path
   * does not clear it at all, and the conversation came back carrying a document from the cycle the
   * learner had just left.
   *
   * So the panel loads the library itself and remembers which cycle it loaded. Neither an empty bag
   * nor a full one is taken on trust.
   */
  private withCycleLibrary(use: () => void): void {
    const pathId = this.appState.get('currentPath')?.id;
    if (!pathId) {
      // No cycle to check a document against, so nothing can be shown as belonging to one.
      this.loaded = true;
      return;
    }
    // Both halves, because each answers a different question. The cycle id says the library was
    // the right one when it was fetched; the identity says appState still holds THAT library rather
    // than one put there since — by a switch that refilled it, or one that emptied it. An empty
    // library of our own is still an answer, and `[]` is truthy, so it takes this branch.
    if (this.casualLibraryPathId === pathId
      && this.casualLibrary
      && this.appState.get('casualDocuments') === this.casualLibrary) {
      use();
      return;
    }
    this.libraryService.getCasualDocuments({ pathId })
      // One answer, one `use()`. An HttpClient GET emits once and completes, so this changes
      // nothing today — it is here so that stays true of whatever supplies the library later, `use`
      // being a caller's callback that writes to the transcript.
      .pipe(take(1), takeUntil(this.destroyed$))
      .subscribe(
        (res: any) => {
          this.casualLibraryPathId = pathId;
          // Held by reference as well as put in the bag, and the SAME array in both, so the gate
          // above can tell our library from whatever may replace it.
          this.casualLibrary = Array.isArray(res?.data) ? res.data : [];
          // Stored where the rest of the app reads it — correcting a bag left holding the previous
          // cycle's documents — so the wait is paid once rather than per conversation opened.
          this.appState.set('casualDocuments', this.casualLibrary);
          use();
          this.cdRef.detectChanges();
        },
        () => {
          // No library to match against. The transcript still reads; releasing the skeleton is the
          // difference between "no attachment" and "loading, for ever".
          this.loaded = true;
        },
      );
  }

  /**
   * The transcript shape the panel renders, for a conversation opened from History. Shares
   * buildChatRow with the resume path so the same stored message looks the same either way.
   */
  private mapSessionMessages(messages: any[]): any[] {
    const rows = (messages || []).reduce((acc: any[], message: any) => {
      const row = this.buildChatRow(message);
      if (row) { acc.push(row); }
      return acc;
    }, []);
    return this.enableActionsOnLatestAnswer(rows);
  }

  /**
   * Puts the copy / like / dislike bar under the newest coach answer, which is the only row that
   * ever carries it.
   *
   * That is what `noop` does live: a bubble has no bar while it is being typed, gains one when the
   * typewriter finishes, and loses it again as soon as the next question is asked. It has to stay
   * one bar, because isLikeSelected and isCopyContent are per panel rather than per row — several
   * bars at once would all highlight together when one answer was liked.
   *
   * Rebuilt rows arrive with noop true, so a transcript put back on screen had no bar at all: a
   * refresh, a path switch, coming back from an in-step screen and opening a conversation from
   * History all lost the options the learner had a moment earlier.
   */
  private enableActionsOnLatestAnswer(rows: any[]): any[] {
    for (let i = (rows || []).length - 1; i >= 0; i--) {
      // Skipping action cards: the bar is hidden under those anyway, and stopping on one would
      // leave the answer above it without its options.
      if (rows[i]?.role === 'foozi-response' && !rows[i].actionTitle) {
        rows[i].noop = false;
        break;
      }
    }
    return rows;
  }

  /**
   * One stored message → one chat row, or null when there is nothing to show. The single place that
   * decides role, styling, post-link rewriting and which action fields a row carries — used both
   * when resuming the live session and when opening a past one from History.
   */
  private buildChatRow(message: any): any | null {
    const isAssistant = message?.role === 'assistant' || message?.role === 'coach';
    const isUser = message?.role === 'user';
    const role = isAssistant ? 'foozi-response' : isUser ? 'user' : 'system';
    const classValue = isAssistant ? 'foozi-responz-wrap'
      : isUser ? 'justify-content-end user-input-wraper'
      : 'info-event-wrap';

    const contentStr = typeof message?.content === 'string' ? message.content : '';
    const actionData = message?.action || null;
    if (!contentStr.trim() && !actionData) { return null; }

    const modifiedMessage = contentStr.replace(
      /\[post_id-(\d+)-([\w\s.]+)-?\]/g,
      (_match: any, postId: any, name: any) => ` (<span class="post-link"><span class=${postId}>See ${name}'s post</span></span>)`
    )?.replace(/\s*(?:\[path_id-\d+-step-\d+\]|\[s*path.*?step-\d+]|\[s*path<\/em>id-\d+-step-\d+])\s*/g, '');

    const msgSources: any[] = message?.sources || [];
    return {
      role,
      message: modifiedMessage,
      actionTitle: role === 'foozi-response' ? (actionData?.title || null) : null,
      actionDescRaw: role === 'foozi-response' ? (actionData?.description || null) : null,
      actionDescHtml: role === 'foozi-response' && actionData?.description
        ? this.sanitizer.bypassSecurityTrustHtml(actionData.description)
        : null,
      screenContentId: role === 'foozi-response' && actionData
        ? (message?.screen_content_id || null)
        : null,
      sources: role === 'foozi-response' && msgSources.length > 0 ? msgSources : null,
      eventType: message?.type || null,
      class: classValue,
      noop: true,
    };
  }

  /** The document ids a message cites, in either the tool_id or the older doc_id form. */
  private docIdsFrom(message: any): number[] {
    const ids = (message?.sources || [])
      .map((source: any) => source?.tool_id ?? source?.doc_id)
      .filter((docId: any) => !!docId)
      .map((docId: any) => +docId);
    return [...new Set<number>(ids)];
  }

  private async openCompletedFeedbackModal(state: MicroActionSessionState, onDismiss: () => void): Promise<void> {
    const modal = await this.modalController.create({
      component: CompletedReviewModalComponent,
      componentProps: { currentHabit: state },
      cssClass: this.homeUiService.isDark()
        ? 'dark-theme-review-modal mobile-custom-to_do_list-forms'
        : 'light-theme mobile-custom-to_do_list-forms',
    });
    modal.onDidDismiss().then(() => onDismiss());
    await modal.present();
  }

  reviseInStepAction() {
    const state = this.microActionState;
    if (!state) return;
    this.actionSessionFor(state).pipe(
      switchMap((sessionId) => this.fooziAiConciergeService.customizeMicroAction(sessionId, state.scid, {
        prompt: 'Please help me revise this action',
      })),
      takeUntil(this.destroyed$),
    ).subscribe((res) => {
      const msg = (res?.message && typeof res.message === 'object') ? res.message : res;
      const newAction = msg?.action;
      if (newAction) {
        this.microActionStateService.update({ ...state, currentAction: newAction });
      }
    });
  }

  ngOnDestroy(): void {
    this.fooziAiConciergeService.panelUnmounted();
    if (this.messageLimitNoticeTimer !== null) {
      clearTimeout(this.messageLimitNoticeTimer);
      this.messageLimitNoticeTimer = null;
    }
    if (this.activeTypingInterval !== null) {
      clearInterval(this.activeTypingInterval);
      this.activeTypingInterval = null;
    }
    this.removeInputEventListeners();
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
